import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import './SettingsPage.css';
import WastageService from '../services/wastageService';
import authService from '../services/authService';
import UserService from '../services/userService';
import RoleService from '../services/roleService';
import SettingsService from '../services/settingsService';
import http from '../services/httpClient';
import InventoryCategoriesService from '../services/inventoryCategoriesService';
import categoryEventService from '../services/categoryEventService';
import wastageReasonEventService from '../services/wastageReasonEventService';
import UnitMappingService from '../services/unitMappingService';
// PIECE GUARD — every "pieces or packets?" answer, with the owner's way to correct one and
// the purchases that were saved under it.
import PieceGuardSettings from './PieceGuardSettings';
// STOCK-IN SAFETY ENGINE (Layer 4) — Settings › Price history, read only. Listed only while
// /api/cx/settings says history_enabled for this business; otherwise nothing here changes.
import PriceHistorySettings from './PriceHistorySettings';
import LastPurchasesSettings from './LastPurchasesSettings';
import { cxGetSettings } from '../services/cxGuardClient';
import BusinessHoursService from '../services/businessHoursService';
import businessHoursEventService from '../services/businessHoursEventService';
import HealthService from '../services/healthService';
import { WastageThresholdsApi } from '../services/wastageThresholdsApi';
import { AuthContext } from '../contexts/AuthContext';
import { useTranslate } from '../translations/useTranslate';
import LanguageSelector from './LanguageSelector';
import PortionMultiplierSettings from './PortionMultiplierSettings';
import AuditLogViewer from './AuditLogViewer';
import CustomizationRules from './CustomizationRules';
import ItemMappingLogModal from './ItemMappingLogModal';
import MenuOrganization from './menuorganization';
import { bmSettingsVisible } from '../billMemory/client';
import useNetProfitHeadline from './netProfit/useNetProfitHeadline';

// Import icons from a React icon library (you may need to install one like react-icons)
import { 
  FaUser, FaBuilding, FaBox, FaShoppingCart, FaTruck, FaTrash, 
  FaUsers, FaQuestionCircle, FaChevronRight, FaChevronDown, 
  FaChevronUp, FaBell, FaTimes, FaSearch, FaUserPlus, FaEye, FaEyeSlash, FaArrowLeft,
  FaFilter, FaChartLine
} from 'react-icons/fa';

const CLOSING_STOCK_CORRECTION_TOGGLE_ID = 'closing-stock-correction';
const NEGATIVE_STOCK_FIX_TOGGLE_ID = 'negative-stock-fix';
const VENDOR_AUTOFILL_PROMPT_TOGGLE_ID = 'vendor-autofill-prompt';
// Reports price a negative-stock ingredient at its last known purchase price instead
// of showing a dash. Sits next to NEGATIVE_STOCK_FIX_TOGGLE_ID because the two sound
// alike, and users must be able to see the difference: that one WRITES stock-in
// records, this one only changes what the report displays.
const NEGATIVE_STOCK_PRICE_FALLBACK_TOGGLE_ID = 'negative-stock-price-fallback';
// Mirrors DEFAULT_NEGATIVE_STOCK_FALLBACK_ENABLED in backend/utils/businessSettings.js.
// One name on each side, so the next person to change the default has one thing to grep
// for instead of two booleans that drift apart silently.
const NEGATIVE_STOCK_PRICE_FALLBACK_DEFAULT = true;
// B8. The staleness window for the estimate above. Its own row rather than a number tucked
// into the toggle's subtitle: it is a different decision (how old is too old) made at a
// different time (when a dash refuses to clear), and burying it is what turns a
// configurable limit into a support ticket.
const NEGATIVE_STOCK_PRICE_MAX_AGE_ID = 'negative-stock-price-max-age';
// Matches DEFAULT_MAX_PRICE_AGE_DAYS in backend/utils/businessSettings.js. Duplicated
// deliberately — the UI must render a number before the settings fetch returns, and
// showing a different default from the one actually in force would be worse than a copy.
const DEFAULT_PRICE_MAX_AGE_DAYS = 90;

// Countdown for the closing-stock revert window (7 days from submission).
//   > 24h  →  "Nd remaining"      (e.g. 7d, 5d, 2d …)
//   ≤ 24h  →  "N hrs remaining"   (24 hrs, 12 hrs …)
//   ≤ 60m  →  live "MM:SS left"   (ticks every second)
// Self-contained so only this chip re-renders on each tick, not all of Settings.
const RevertCountdown = ({ submittedAt, windowDays = 7 }) => {
  const deadline = useMemo(
    () => new Date(submittedAt).getTime() + windowDays * 24 * 60 * 60 * 1000,
    [submittedAt, windowDays]
  );
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const rem = deadline - now;
  const H = 3600000, D = 86400000;
  let label, urgent = false;
  if (rem <= 0) {
    label = 'Window expired';
    urgent = true;
  } else if (rem < H) {
    const m = Math.floor(rem / 60000);
    const s = Math.floor((rem % 60000) / 1000);
    label = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} left`;
    urgent = true;
  } else if (rem < D) {
    const hrs = Math.ceil(rem / H);
    label = `${hrs} hr${hrs === 1 ? '' : 's'} remaining`;
  } else {
    label = `${Math.ceil(rem / D)}d remaining`;
  }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 999, fontWeight: 700, fontSize: 12,
      background: urgent ? '#fef2f2' : '#eef2ff',
      color: urgent ? '#b91c1c' : '#3730a3',
      border: `1px solid ${urgent ? '#fecaca' : '#c7d2fe'}`
    }}>
      &#9203; {label}
    </span>
  );
};

const SettingsPage = ({ goTo, currentUser, setCurrentUser, onNavigateToUnits, onNavigateToVendors, screenParams, onOpenFromSettings }) => {
  // Strip inline styles that override borders for Invite User inputs
  React.useEffect(() => {
    const stripInlineForInviteInputs = () => {
      const nodes = document.querySelectorAll('.sp-modal-container input.iu-input');
      nodes.forEach((el) => {
        if (el && el.getAttribute('style')) {
          el.removeAttribute('style');
        }
      });
    };
    // Run on mount and after modal toggles
    stripInlineForInviteInputs();
    const observer = new MutationObserver(() => stripInlineForInviteInputs());
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, []);

  // Disable footer navigation while ANY modal is open (DOM-based, avoids state ordering issues)
  React.useEffect(() => {
    const toggleByOverlayPresence = () => {
      const hasOverlay = document.querySelector('.sp-modal-overlay') !== null;
      document.body.classList.toggle('modal-open', Boolean(hasOverlay));
    };
    toggleByOverlayPresence();
    const observer = new MutationObserver(toggleByOverlayPresence);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      document.body.classList.remove('modal-open');
    };
  }, []);
  
  // Get logout function from AuthContext
  const { logout } = useContext(AuthContext);
  
  // Get our custom translation hook
  const { t, language: currentLang, changeLanguage } = useTranslate();
  
  const [expandedSections, setExpandedSections] = useState(['account']);
  // Live figures for Financials & Profitability, read only while that section is open.
  const npHeadline = useNetProfitHeadline(expandedSections.includes('financials'));
  const [settingsSearchQuery, setSettingsSearchQuery] = useState('');

  // Handle auto-expanding section from navigation params
  useEffect(() => {
    if (screenParams?.expandSection) {
      const sectionId = screenParams.expandSection;
      setExpandedSections(prev => {
        if (prev.includes(sectionId)) return prev;
        return [...prev, sectionId];
      });
      // Auto-scroll to the section after expansion
      setTimeout(() => {
        const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
        if (sectionElement) {
          sectionElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest'
          });
        }
      }, 300);
    }
  }, [screenParams]);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState({ email: '', password: '' });
  const [showRolePermissionsModal, setShowRolePermissionsModal] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [showWastageReasonsModal, setShowWastageReasonsModal] = useState(false);
  const [showDeviationModal, setShowDeviationModal] = useState(false);
  const [showOperatingHoursModal, setShowOperatingHoursModal] = useState(false);
  const [showPortionMultipliersModal, setShowPortionMultipliersModal] = useState(false);
  const [showCustomizationRulesModal, setShowCustomizationRulesModal] = useState(false);
  const [showItemMappingLogModal, setShowItemMappingLogModal] = useState(false);
  const [showPieceGuardSettings, setShowPieceGuardSettings] = useState(false);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [showLastPurchases, setShowLastPurchases] = useState(false);
  // Sales Name Check (spec §10.5): listed once the onboarding check is done or
  // skipped, or when the business is live. One quiet call; if it fails, the
  // account's onboarded flag decides.
  const [sncVisible, setSncVisible] = useState(false);
  useEffect(() => {
    let alive = true;
    const onboarded = Boolean(currentUser && (currentUser.isOnboarded === true || currentUser.is_onboarded === true));
    http.get('/sales-name-check/status')
      .then((s) => {
        if (!alive) return;
        if (s && s.success === true) {
          setSncVisible(s.isOnboarding !== true || s.state === 'done' || s.state === 'skipped');
        } else {
          setSncVisible(onboarded);
        }
      })
      .catch(() => { if (alive) setSncVisible(onboarded); });
    return () => { alive = false; };
  }, [currentUser]);
  // Bill Memory (design §15): listed when Bill Memory is not Off for the business (Shadow too) and
  // this user may read it (stock_in or more); Invexis admins see it in any mode. One quiet call;
  // a failure hides it.
  const [bmVisible, setBmVisible] = useState(false);
  useEffect(() => {
    let alive = true;
    http.get('/bill-memory/access')
      .then((a) => { if (alive) setBmVisible(bmSettingsVisible(a)); })
      .catch(() => { if (alive) setBmVisible(false); });
    return () => { alive = false; };
  }, [currentUser]);
  const [cxHistoryOn, setCxHistoryOn] = useState(false);
  // One quiet call; any failure (not installed, offline) leaves the entry hidden.
  useEffect(() => {
    let alive = true;
    cxGetSettings()
      .then((cx) => { if (alive && cx && cx.history_enabled === true) setCxHistoryOn(true); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: 'Message', message: '', tone: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onResolve: null });
  const [showBackgroundImageModal, setShowBackgroundImageModal] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [, setShowPerformanceMeterModal] = useState(false);
  const [performanceMeterData, setPerformanceMeterData] = useState({
    score: 0,
    backendStatus: 'unknown',
    databaseStatus: 'unknown',
    latencyMs: 0,
    checkedAt: null
  });
  const [isNegativeStockFixRunning, setIsNegativeStockFixRunning] = useState(false);
  // Revert Closing Stock flow
  const [revertModal, setRevertModal] = useState({ open: false, loading: false, busy: false, preview: null, error: null, done: null });
  const [deviation, setDeviation] = useState(5);
  const [customDeviation, setCustomDeviation] = useState('');
  // ✅ FIX: Use the same method as StockInForm to get businessId
  const businessId = UnitMappingService.getBusinessId();

  // ── Revert Closing Stock ────────────────────────────────────
  // Opens the flow: fetch a preview of the latest count + whether it can be
  // reverted (blocked reason surfaced verbatim), then let the user confirm.
  const openRevertClosingStock = async () => {
    setRevertModal({ open: true, loading: true, busy: false, preview: null, error: null, done: null });
    try {
      const resp = await http.get(`/closing-stock/revert-latest/preview?businessId=${businessId}`);
      setRevertModal(m => ({ ...m, loading: false, preview: resp }));
    } catch (e) {
      setRevertModal(m => ({ ...m, loading: false, error: e.message || 'Could not load the latest closing stock.' }));
    }
  };

  const doRevertClosingStock = async () => {
    setRevertModal(m => ({ ...m, busy: true, error: null }));
    try {
      const resp = await http.post('/closing-stock/revert-latest', { businessId });
      // A truthy response is NOT a success. httpClient RETURNS rather than
      // throws on a 403 (permission denied) and after a 401 token refresh, so
      // both used to render the green "Reverted." banner while nothing had
      // happened. Only the explicit flag counts.
      if (!resp || resp.success !== true) {
        setRevertModal(m => ({
          ...m, busy: false,
          error: resp?.error || 'Revert failed. Please try again.'
        }));
        return;
      }
      setRevertModal(m => ({ ...m, busy: false, done: resp }));
    } catch (e) {
      // The server's neat reason (e.g. "A sale has been recorded…") is on e.message.
      setRevertModal(m => ({ ...m, busy: false, error: e.message || 'Revert failed.' }));
    }
  };

  // Get user role from Redis via API (no localStorage)
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    // Fetch current user info from backend (Redis session via JWT)
    const fetchUserRole = async () => {
      try {
        const response = await authService.checkAuthStatus();
        
        if (response?.user?.role_name) {
          setUserRole(response.user.role_name);
        } else if (response?.user?.roleName) {
          setUserRole(response.user.roleName);
        }
      } catch (error) {
        console.error('   ❌ Failed to fetch user role:', error);
        // Don't throw or show error - settings page can still work
        // Error handling is done by httpClient (redirect to login if needed)
      }
    };
    
    fetchUserRole();
  }, []);

  // Load persisted business settings on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await SettingsService.getSettings(businessId, [
          'app.language',
          'vendors.default_payment_terms_days',
          'wastage.yield_tolerance_pct',
          'inventory.closing_stock_correction_enabled',
          'sales.tax_for_stock_in_enabled',
          'inventory.vendor_autofill_prompt_enabled',
          'reports.negative_stock_price_fallback_enabled',
          'reports.negative_stock_price_fallback_max_age_days'
        ]);
        const map = Object.fromEntries((res.data || []).map(s => [s.key, s.value]));
        if (map['app.language']) setSelectedLanguage(map['app.language']);
        if (map['vendors.default_payment_terms_days']) setPaymentTermDays(Number(map['vendors.default_payment_terms_days']));
        if (map['wastage.yield_tolerance_pct']) setDeviation(Number(map['wastage.yield_tolerance_pct']));
        if (Object.prototype.hasOwnProperty.call(map, 'inventory.closing_stock_correction_enabled')) {
          const enabled = map['inventory.closing_stock_correction_enabled'] === true
            || String(map['inventory.closing_stock_correction_enabled']).toLowerCase() === 'true';
          setToggleStates((prev) => ({
            ...prev,
            [CLOSING_STOCK_CORRECTION_TOGGLE_ID]: enabled,
          }));
          localStorage.setItem(`closingStockCorrectionEnabled_${businessId}`, enabled ? 'true' : 'false');
        }
        if (Object.prototype.hasOwnProperty.call(map, 'sales.tax_for_stock_in_enabled')) {
          const enabled = map['sales.tax_for_stock_in_enabled'] === true
            || String(map['sales.tax_for_stock_in_enabled']).toLowerCase() === 'true';
          setToggleStates((prev) => ({
            ...prev,
            'tax-for-stock-in': enabled,
          }));
          localStorage.setItem(`taxForStockInEnabled_${businessId}`, enabled ? 'true' : 'false');
        }
        {
          // Deliberately NOT guarded by hasOwnProperty, unlike its neighbours: this
          // setting's default is ON, so "no stored row" is a state the switch has to
          // paint rather than one it can skip. Guarding it would leave the initial
          // state standing, which happens to be right today — and would silently
          // become wrong the moment that initial value is edited.
          const stored = map['reports.negative_stock_price_fallback_enabled'];
          const enabled = Object.prototype.hasOwnProperty.call(map, 'reports.negative_stock_price_fallback_enabled')
            ? (stored === true || String(stored).toLowerCase() === 'true')
            : NEGATIVE_STOCK_PRICE_FALLBACK_DEFAULT;
          setToggleStates((prev) => ({
            ...prev,
            [NEGATIVE_STOCK_PRICE_FALLBACK_TOGGLE_ID]: enabled,
          }));
        }
        if (Object.prototype.hasOwnProperty.call(map, 'reports.negative_stock_price_fallback_max_age_days')) {
          const days = Number(map['reports.negative_stock_price_fallback_max_age_days']);
          // A stored value that is negative or non-numeric is meaningless. The backend
          // already falls back to the default in that case; mirroring the rule here keeps
          // the screen from displaying a limit that is not the one being applied.
          if (Number.isFinite(days) && days >= 0) setNegativeStockPriceMaxAge(days);
        }
        if (Object.prototype.hasOwnProperty.call(map, 'inventory.vendor_autofill_prompt_enabled')) {
          const enabled = map['inventory.vendor_autofill_prompt_enabled'] === true
            || String(map['inventory.vendor_autofill_prompt_enabled']).toLowerCase() === 'true';
          setToggleStates((prev) => ({
            ...prev,
            [VENDOR_AUTOFILL_PROMPT_TOGGLE_ID]: enabled,
          }));
          localStorage.setItem(`vendorAutofillPromptEnabled_${businessId}`, enabled ? 'true' : 'false');
        }
      } catch (e) { 
        console.error('Failed to load settings:', e);
        // Don't throw - settings page can still work with defaults
        // Error handling is done by httpClient (redirect to login if needed)
      }
      
      // Load closing stock lock status on mount
      if (businessId) {
        try {
          const lockRes = await http.get(`/closing-stock/lock-status?businessId=${businessId}`);
          if (lockRes && lockRes.success) {
            setToggleStates((prev) => ({
              ...prev,
              'closing-stock-locked': lockRes.locked,
            }));
          }
        } catch (lockError) {
          console.error('Failed to load closing stock lock status:', lockError);
        }
      }
      
      // Load background image from localStorage
      const savedBackground = localStorage.getItem('loginBackgroundImage');
      if (savedBackground) {
        setBackgroundImagePreview(savedBackground);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    const handleToggleUpdate = (event) => {
      const detail = event?.detail || {};
      const eventBusinessId = Number(detail.businessId || 0);
      const currentBusinessId = Number(businessId || 0);

      if (eventBusinessId > 0 && currentBusinessId > 0 && eventBusinessId !== currentBusinessId) {
        return;
      }

      if (typeof detail.enabled !== 'boolean') {
        return;
      }

      setToggleStates((prev) => ({
        ...prev,
        [CLOSING_STOCK_CORRECTION_TOGGLE_ID]: detail.enabled,
      }));

      if (currentBusinessId > 0) {
        localStorage.setItem(`closingStockCorrectionEnabled_${currentBusinessId}`, detail.enabled ? 'true' : 'false');
      }
    };

    window.addEventListener('inventory:closing-stock-correction-toggle-updated', handleToggleUpdate);
    return () => {
      window.removeEventListener('inventory:closing-stock-correction-toggle-updated', handleToggleUpdate);
    };
  }, [businessId]);
  
  // Canonical alert keys. These MUST match backend/utils/notificationPrefs.js
  // (email*) and services/reportScheduler.js (in-app). Default-on everywhere:
  // only an explicit false suppresses a notification.
  const [notificationSettings, setNotificationSettings] = useState({
    // Email — sent by services/watchdogScheduler.js.
    // emailMissedTsr is intentionally absent: it is in MANDATORY_ALERT_TYPES
    // on the backend and can never be switched off.
    emailDailyPulse: true,
    emailRadarAlerts: true,
    emailMonthlyAudit: true,
    // In-app — created by services/reportScheduler.js
    endOfDayReports: true,
    dailyReportReminders: true,
    monthlyReports: true,
    performanceAlerts: true,
  });

  const [inviteUserData, setInviteUserData] = useState({
    name: '',
    role: ''
  });
  const [inviteUserErrors, setInviteUserErrors] = useState({
    name: '',
    role: ''
  });
  const [customRole, setCustomRole] = useState('');
  const [waiterLimit, setWaiterLimit] = useState(0);
  const [waiterLimitError, setWaiterLimitError] = useState('');

  // Check if invite form is valid
  const isInviteFormValid = () => {
    const nameValid = inviteUserData.name.trim().length > 0;
    const roleValid = inviteUserData.role.trim().length > 0;
    return nameValid && roleValid;
  };

  const [negativeStockPriceMaxAge, setNegativeStockPriceMaxAge] = useState(DEFAULT_PRICE_MAX_AGE_DAYS);
  const [toggleStates, setToggleStates] = useState({
    // Keys must match item.id values used below
    'pricing-control': false,
    [CLOSING_STOCK_CORRECTION_TOGGLE_ID]: false,
    [NEGATIVE_STOCK_FIX_TOGGLE_ID]: false,
    // Default ON, matching DEFAULT_NEGATIVE_STOCK_FALLBACK_ENABLED in
    // backend/utils/businessSettings.js. These two must agree: the backend treats an
    // absent setting as ON, so a screen that painted the switch OFF would be telling the
    // owner their reports are strict while they are in fact being estimated.
    [NEGATIVE_STOCK_PRICE_FALLBACK_TOGGLE_ID]: NEGATIVE_STOCK_PRICE_FALLBACK_DEFAULT,
    'tax-for-stock-in': false,
    [VENDOR_AUTOFILL_PROMPT_TOGGLE_ID]: false,
    'closing-stock-locked': false,
  });

  const [rolePermissionStates, setRolePermissionStates] = useState({});
  const [rolePermissionsLoading, setRolePermissionsLoading] = useState(false);
  const [actualPermissions, setActualPermissions] = useState({});
  const [allAvailablePermissions, setAllAvailablePermissions] = useState([]);
  const [enabledPermissionIds, setEnabledPermissionIds] = useState([]);
  const [permissionsSaving, setPermissionsSaving] = useState(false);

  // Wastage reasons state
  const [wastageReasons, setWastageReasons] = useState([]);
  const [newReasonLabel, setNewReasonLabel] = useState('');
  const [newReasonCategory, setNewReasonCategory] = useState('General Waste');
  
  // Custom dropdown states
  const [wasteCategoryDropdownOpen, setWasteCategoryDropdownOpen] = useState(false);
  const [taxTypeDropdownOpen, setTaxTypeDropdownOpen] = useState(false);
  const [taxInclusionDropdownOpen, setTaxInclusionDropdownOpen] = useState(false);

  // Operating hours state
  const [operatingHours, setOperatingHours] = useState({
    Monday: { 
      isOpen: true, 
      shifts: {
        morning: { enabled: true, start: '9:00 AM', end: '12:00 PM' },
        afternoon: { enabled: false, start: '1:00 PM', end: '5:00 PM' },
        evening: { enabled: false, start: '6:00 PM', end: '10:00 PM' }
      }
    },
    Tuesday: { 
      isOpen: true, 
      shifts: {
        morning: { enabled: true, start: '9:00 AM', end: '12:00 PM' },
        afternoon: { enabled: false, start: '1:00 PM', end: '5:00 PM' },
        evening: { enabled: false, start: '6:00 PM', end: '10:00 PM' }
      }
    },
    Wednesday: { 
      isOpen: true, 
      shifts: {
        morning: { enabled: true, start: '9:00 AM', end: '12:00 PM' },
        afternoon: { enabled: false, start: '1:00 PM', end: '5:00 PM' },
        evening: { enabled: false, start: '6:00 PM', end: '10:00 PM' }
      }
    },
    Thursday: { 
      isOpen: true, 
      shifts: {
        morning: { enabled: true, start: '9:00 AM', end: '12:00 PM' },
        afternoon: { enabled: false, start: '1:00 PM', end: '5:00 PM' },
        evening: { enabled: false, start: '6:00 PM', end: '10:00 PM' }
      }
    },
    Friday: { 
      isOpen: true, 
      shifts: {
        morning: { enabled: true, start: '9:00 AM', end: '12:00 PM' },
        afternoon: { enabled: false, start: '1:00 PM', end: '5:00 PM' },
        evening: { enabled: false, start: '6:00 PM', end: '10:00 PM' }
      }
    },
    Saturday: { 
      isOpen: true, 
      shifts: {
        morning: { enabled: true, start: '9:00 AM', end: '12:00 PM' },
        afternoon: { enabled: false, start: '1:00 PM', end: '5:00 PM' },
        evening: { enabled: false, start: '6:00 PM', end: '10:00 PM' }
      }
    },
    Sunday: { 
      isOpen: false, 
      shifts: {
        morning: { enabled: false, start: '9:00 AM', end: '12:00 PM' },
        afternoon: { enabled: false, start: '1:00 PM', end: '5:00 PM' },
        evening: { enabled: false, start: '6:00 PM', end: '10:00 PM' }
      }
    }
  });

  // Edit Profile modal state
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [profileFormErrors, setProfileFormErrors] = useState({ name: '', email: '', phone: '' });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);

  const showAppAlert = React.useCallback((message) => {
    const raw = String(message || '').trim();
    const cleaned = raw
      .replace(/^❌\s*/u, '')
      .replace(/^✅\s*/u, '')
      .replace(/^⚠️\s*/u, '')
      .replace(/^Error:\s*/i, '')
      .trim();

    setAlertDialog({ open: true, title: 'Message', message: cleaned, tone: 'info' });
  }, []);

  const hideAppAlert = React.useCallback(() => {
    setAlertDialog({ open: false, title: 'Message', message: '', tone: 'info' });
  }, []);

  const showAppConfirm = React.useCallback((message) => {
    return new Promise((resolve) => {
      setConfirmDialog({ open: true, message: String(message || ''), onResolve: resolve });
    });
  }, []);

  const handleConfirmChoice = React.useCallback((choice) => {
    if (typeof confirmDialog.onResolve === 'function') {
      confirmDialog.onResolve(choice);
    }
    setConfirmDialog({ open: false, message: '', onResolve: null });
  }, [confirmDialog]);

  // Route Settings alerts to project-styled modal instead of browser popups.
  const alert = React.useCallback((message) => {
    showAppAlert(message);
  }, [showAppAlert]);

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate phone number (10 digits)
  const isValidPhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  // Check if profile form is complete and valid
  const isProfileFormValid = () => {
    const nameValid = profileForm.name.trim().length > 0;
    const emailValid = profileForm.email.trim().length > 0 && isValidEmail(profileForm.email);
    const phoneValid = profileForm.phone.trim().length > 0 && isValidPhone(profileForm.phone);
    return nameValid && emailValid && phoneValid;
  };

  // Initialize profile form with current user data
  useEffect(() => {
    if (currentUser) {
      setProfileForm({
        name: currentUser.fullName || currentUser.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || currentUser.phoneNumber || ''
      });
      
      // Set current profile picture if exists
      if (currentUser.profilePicture) {
        setProfilePicturePreview(currentUser.profilePicture);
      }
    }
  }, [currentUser]);

  // Payment Methods modal state
  const [showPaymentMethodsModal, setShowPaymentMethodsModal] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 'cash', label: 'Cash' },
    { id: 'upi', label: 'UPI' },
    { id: 'card', label: 'Card' },
  ]);
  const [newPaymentLabel, setNewPaymentLabel] = useState('');

  // Menu Categories modal state
  const [showMenuCategoriesModal, setShowMenuCategoriesModal] = useState(false);
  const [showMenuOrganizationModal, setShowMenuOrganizationModal] = useState(false);
  const [menuCategories, setMenuCategories] = useState([
    { id: 1, name: 'Appetizers', color: '#FF6B6B', displayOrder: 1 },
    { id: 2, name: 'Main Course', color: '#4ECDC4', displayOrder: 2 },
    { id: 3, name: 'Desserts', color: '#FFD93D', displayOrder: 3 },
    { id: 4, name: 'Beverages', color: '#95E1D3', displayOrder: 4 },
  ]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', color: '#FF6B6B', displayOrder: '' });

  // Tax Rates modal state
  const [showTaxRatesModal, setShowTaxRatesModal] = useState(false);
  const [taxRates, setTaxRates] = useState([
    { id: 1, name: 'GST - CGST', rate: 9, type: 'standard', inclusionRule: 'exclusive', isActive: true },
    { id: 2, name: 'GST - SGST', rate: 9, type: 'standard', inclusionRule: 'exclusive', isActive: true },
    { id: 3, name: 'Service Charge', rate: 10, type: 'standard', inclusionRule: 'exclusive', isActive: true },
  ]);
  const [editingTax, setEditingTax] = useState(null);
  const [newTax, setNewTax] = useState({ name: '', rate: '', type: 'standard', inclusionRule: 'exclusive', isActive: true });

  // Localized Pricing modal state
  const [showLocalizedPricingModal, setShowLocalizedPricingModal] = useState(false);
  const [pricingSettings, setPricingSettings] = useState({
    deliveryMarkup: 20,
    takeoutMarkup: 0,
  });
  const [itemPriceOverrides, setItemPriceOverrides] = useState([
    { id: 1, itemName: 'Cheesy Pizza', basePrice: 200, takeoutPrice: 190, deliveryPrice: 240 },
    { id: 2, itemName: 'Pasta Alfredo', basePrice: 180, takeoutPrice: 180, deliveryPrice: 216 },
  ]);
  const [searchItemQuery, setSearchItemQuery] = useState('');
  const [editingItemPrice, setEditingItemPrice] = useState(null);

  // Restaurant Profile modal state
  const [showRestaurantProfileModal, setShowRestaurantProfileModal] = useState(false);
  const [restaurantProfile, setRestaurantProfile] = useState({
    businessName: 'Invexis Restaurant',
    address: '123 MG Road, Jubilee Hills',
    contactDetails: '+91 9876543210'
  });

  // Manage Locations modal state
  const [showManageLocationsModal, setShowManageLocationsModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('Banjara Hills');
  
  
  // Default Payment Terms modal state
  const [showPaymentTermsModal, setShowPaymentTermsModal] = useState(false);
  const [paymentTermDays, setPaymentTermDays] = useState(7);
  const [customPaymentTerm, setCustomPaymentTerm] = useState('');

  // Payment Settings modal state (UPI Configuration)
  const [showPaymentSettingsModal, setShowPaymentSettingsModal] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({
    paymentMethod: 'razorpay', // 'upi' or 'razorpay'
    vpa: '', // Virtual Payment Address
    merchantUpiLink: null,
    businessName: ''
  });
  const [loadingPaymentSettings, setLoadingPaymentSettings] = useState(false);
  const [paymentMethodDropdownOpen, setPaymentMethodDropdownOpen] = useState(false);

  // Inventory Categories modal state (was UI-only) -> load list when open
  const [showInventoryCategoriesModal, setShowInventoryCategoriesModal] = useState(false);
  const [inventoryCategories, setInventoryCategories] = useState([
    'Produce', 'Dairy', 'Meat', 'Dry Goods', 'Beverages', 'Packaging'
  ]);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  useEffect(() => {
    if (!showInventoryCategoriesModal) return;
    (async () => {
      try {
        const res = await InventoryCategoriesService.list(businessId);
        setInventoryCategories((res.data || []).map(c => c.name));
      } catch (e) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInventoryCategoriesModal]);

  // Change Password modal state (verify old password via backend)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePwdStep, setChangePwdStep] = useState(1); // 1: old, 2: new
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Manage Users: role change modal (UI-only)
  const [showRoleChangeModal, setShowRoleChangeModal] = useState(false);
  const [roleChangeMode, setRoleChangeMode] = useState('promote'); // 'promote' | 'demote'
  const [targetUser, setTargetUser] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [rolesError, setRolesError] = useState(null);

  useEffect(() => {
    const loadReasons = async () => {
      try {
        const res = await WastageService.getWastageReasons();
        setWastageReasons(res.data || []);
      } catch (e) {
        console.error('Failed to load wastage reasons:', e);
      }
    };
    if (showWastageReasonsModal) loadReasons();
  }, [showWastageReasonsModal]);

  const languages = ['English', 'Hindi', 'Telugu'];
  
  // RBAC Roles - 4 official roles based on RBAC document
  const defaultRoles = [
    'Owner/Admin',
    'Manager/Supervisor',
    'Chef/Kitchen',
    'Waiter/Staff'
  ];
  const [roles, setRoles] = useState(defaultRoles);
  
  useEffect(() => {
    (async () => {
      try {
        const res = await RoleService.listRoles(businessId);
        const roleNames = (res.data || []).map(r => r.role_name);
        setAllRoles(res.data || []);
        
        // Merge backend roles with default roles (remove duplicates)
        const mergedRoles = [...new Set([...defaultRoles, ...roleNames])];
        setRoles(mergedRoles);
      } catch (e) {
        console.error('Failed to load roles:', e);
        setRolesError(e.message || 'Failed to load roles');
        // Keep default roles if backend fails
        setRoles(defaultRoles);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultLocations = [
    'Jubilee Hills',
    'Gachibowli', 
    'Banjara Hills',
    'Hitech City',
    'Kondapur'
  ];
  const [locations, setLocations] = useState(defaultLocations);

  // Users state (live from backend)
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);

  // Notification preferences are loaded by a single effect further down
  // (see "After notificationSettings useState initialization"). A duplicate
  // loader used to live here and fired a second identical GET on every mount.

  // Helpers
  const formatRelativeTime = (ts) => {
    if (!ts) return 'Never';
    const d = new Date(ts);
    if (isNaN(d)) return 'Unknown';
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  };

  const mapServerUser = (u) => {
    const status = u.is_active ? (u.last_login_at ? 'Active' : 'Pending') : 'Inactive';
    return {
      id: String(u.user_id),
      name: u.name || 'Unnamed',
      email: u.email || '',
      role: u.role_name || 'Staff',
      location: 'All Locations',
      lastActive: formatRelativeTime(u.last_login_at || u.created_at),
      status
    };
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      const res = await UserService.getUsers(businessId);
      const rows = Array.isArray(res.data) ? res.data : [];
      setUsers(rows.map(mapServerUser));
    } catch (e) {
      console.error('Failed to load users:', e);
      setUsersError(e.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  // Load once on mount for counts, and refresh whenever the modal opens
  useEffect(() => { loadUsers(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (showUserManagementModal) {
      loadUsers();
      // Also fetch waiter count
      fetchWaiterCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUserManagementModal]);

  // Function to fetch waiter count from backend
  const fetchWaiterCount = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE}/waiter-assignments/${businessId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        const count = result.assignments?.length || 0;
        setWaiterLimit(count);
      } else {
        console.warn('Failed to fetch waiter assignments');
        setWaiterLimit(0);
      }
    } catch (error) {
      console.error('Error fetching waiter count:', error);
      setWaiterLimit(0);
    }
  };

  // Fetch waiter count when invite modal opens
  useEffect(() => {
    if (!showInviteUserModal) return;
    fetchWaiterCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInviteUserModal]);

  // Check waiter count when role changes
  useEffect(() => {
    const checkWaiterLimit = () => {
      if (!inviteUserData.role) {
        setWaiterLimitError('');
        return;
      }

      // Check if selected role is Waiter/Staff
      const isWaiterRole = inviteUserData.role.toLowerCase().includes('waiter') || 
                          inviteUserData.role.toLowerCase().includes('staff');
      
      if (isWaiterRole) {
        // Count existing waiters
        const existingWaiters = users.filter(u => 
          u.role.toLowerCase().includes('waiter') || 
          u.role.toLowerCase().includes('staff')
        ).length;
        
        
        if (existingWaiters >= waiterLimit && waiterLimit > 0) {
          setWaiterLimitError(`You have exceeded the waiter limit set in Owner Dashboard (${waiterLimit}). Current waiters: ${existingWaiters}/${waiterLimit}`);
        } else {
          setWaiterLimitError('');
        }
      } else {
        setWaiterLimitError('');
      }
    };
    
    checkWaiterLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteUserData.role, users, waiterLimit]);

  // Core Module Permissions (Inventory-based businesses - 8 categories, 32 permissions)
  const corePermissions = {
    'Dashboard Access': ['View Dashboard', 'View Analytics', 'View Sales Overview', 'View Table Map'],
    'Inventory Management': ['View Inventory', 'Create Stock-In', 'Edit Items', 'Delete Items', 'Manage Master Data'],
    'Sales Management': ['View Sales', 'Create Sales', 'Edit Sales', 'Delete Sales', 'View Profit Reports'],
    'Wastage Management': ['View Wastage', 'Log Wastage', 'Edit Wastage', 'Delete Wastage'],
    'Vendor Management': ['View Vendors', 'Create Vendors', 'Edit Vendors', 'Delete Vendors', 'Approve POs'],
    'Reports Access': ['View Basic Reports', 'View Financial Reports', 'Export Reports'],
    'Production Planning': ['View Production', 'Create Production Plans', 'Edit Production', 'Approve Production'],
    'Settings Access': ['View Settings', 'Edit Basic Settings', 'Manage Users', 'System Configuration']
  };

  // QR Billing Module Permissions (QR/QR+Inventory businesses - 3 additional categories)
  const qrBillingPermissions = {
    'Kitchen Display System': [
      'Update Order Status',
      'Use Order Batching',
      'View Prep Times',
      'Edit Prep Times',
      'Flag Kitchen Issues'
    ],
    'Floor Service': [
      'Acknowledge Pickup',
      'Confirm Delivery',
      'Handle Call Waiter',
      'Clear Tables',
      'Transfer Tables',
      'Flag Floor Issues'
    ],
    'Payment Operations': [
      'Process Cash Payment',
      'Apply Discounts'
    ]
  };

  // Determine which permissions to show based on user's module access
  // Check if user has QR Billing module enabled (from backend billing model)
  const hasQRBillingAccess = currentUser?.modules?.includes('qr_billing') || 
                             currentUser?.hasQRBilling;

  // Merge permissions based on access
  const permissions = hasQRBillingAccess 
    ? { ...corePermissions, ...qrBillingPermissions } 
    : corePermissions;

  const toggleSection = (sectionId) => {
    const willExpand = !expandedSections.includes(sectionId);
    
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );

    // Auto-scroll to the section after expansion (especially for bottom sections)
    if (willExpand) {
      setTimeout(() => {
        const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
        if (sectionElement) {
          sectionElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest'
          });
        }
      }, 100); // Small delay to allow expansion animation
    }
  };

  const handleInviteUser = async () => {
    try {
      if (!inviteUserData.name || !inviteUserData.role) {
        alert('Please fill in all required fields');
        return;
      }

      // Check waiter limit before creating user
      if (waiterLimitError) {
        alert('Cannot create user: ' + waiterLimitError);
        return;
      }

      // Map role name -> role_id
      const roleObj = allRoles.find(r => r.role_name === inviteUserData.role);
      if (!roleObj) {
        alert('Selected role not found. Please choose a valid role.');
        return;
      }

      const payload = {
        name: inviteUserData.name.trim(),
        role: inviteUserData.role.trim(),
        role_id: roleObj.role_id,
        business_id: businessId,
        auto_generate_credentials: true
      };

      const res = await UserService.createUser(payload);
      if (!res || res.success !== true) throw new Error(res?.error || 'Failed to create user');

      // Show generated credentials to user
      const generatedEmail = res.data?.email || 'N/A';
      const generatedPassword = res.data?.temporaryPassword || 'Pass@123$';
      
      setGeneratedCredentials({ email: generatedEmail, password: generatedPassword });
      setShowCredentialsModal(true);

      // Refresh users list and close modal
      await loadUsers();
      setShowInviteUserModal(false);
      setInviteUserData({ name: '', role: '' });
    } catch (err) {
      alert(err.message || 'Failed To Invite User');
    }
  };

  // Handle profile picture change
  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setProfilePicture(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload profile picture
  const uploadProfilePicture = async () => {
    if (!profilePicture) return null;
    
    try {
      setUploadingProfilePicture(true);
      const result = await authService.uploadProfilePicture(profilePicture);
      return result.profilePictureUrl;
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      throw error;
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      // Reset errors
      setProfileFormErrors({ name: '', email: '', phone: '' });

      // Validate name
      if (!profileForm.name.trim()) {
        setProfileFormErrors(prev => ({ ...prev, name: 'Name is required' }));
        return;
      }

      // Validate email
      if (!profileForm.email.trim()) {
        setProfileFormErrors(prev => ({ ...prev, email: 'Email is required' }));
        return;
      }
      if (!isValidEmail(profileForm.email)) {
        setProfileFormErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
        return;
      }

      // Validate phone
      if (!profileForm.phone.trim()) {
        setProfileFormErrors(prev => ({ ...prev, phone: 'Phone number is required' }));
        return;
      }
      if (!isValidPhone(profileForm.phone)) {
        setProfileFormErrors(prev => ({ ...prev, phone: 'Phone number must be exactly 10 digits' }));
        return;
      }

      // Upload profile picture if changed
      let profilePictureUrl = null;
      if (profilePicture) {
        try {
          profilePictureUrl = await uploadProfilePicture();
        } catch (error) {
          alert('Failed to upload profile picture. Please try again.');
          return;
        }
      }

      // Update profile via authService
      const updatedUser = await authService.updateProfile({
        name: profileForm.name,
        email: profileForm.email,
        phone_number: profileForm.phone,
        profile_picture: profilePictureUrl
      });

      // Update the current user in App state
      if (setCurrentUser && updatedUser.user) {
        setCurrentUser(updatedUser.user);
      }

      alert('Profile updated successfully!');
      setShowEditProfileModal(false);
      setProfileFormErrors({ name: '', email: '', phone: '' });
      setProfilePicture(null);
    } catch (error) {
      console.error('Profile update failed:', error);
      alert(error.message || 'Failed to update profile. Please try again.');
    }
  };

  const handleChangePassword = async () => {
    try {
      if (!newPassword || !confirmPassword) {
        alert('Please Fill Both Password Fields');
        return;
      }

      if (newPassword !== confirmPassword) {
        alert('Passwords Do Not Match');
        return;
      }

      if (newPassword.length < 6) {
        alert('New password must be at least 6 characters long');
        return;
      }

      // Change password via authService
      await authService.changePassword(oldPassword, newPassword);

      alert('Password changed successfully!');
      setShowChangePasswordModal(false);
      setChangePwdStep(1);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Password change failed:', error);
      alert(error.message || 'Failed to change password. Please try again.');
    }
  };

  // Persisted setting handlers
  const onSelectLanguage = async (language) => {
    // Map UI language names to language codes
    const languageMap = {
      'English': 'en',
      'Hindi': 'hi',
      'Telugu': 'te'
    };
    
    const languageCode = languageMap[language] || 'en';
    
    // CRITICAL FIX: Store in localStorage with the CORRECT key that our translation system uses
    localStorage.setItem('selectedLanguage', languageCode);
    localStorage.setItem('appLanguage', languageCode); // Also set old key for compatibility
    
    // Update our custom translation hook
    changeLanguage(languageCode);
    
    // Update UI state
    setSelectedLanguage(language);
    setShowLanguageModal(false);
    
    // Save to database
    try {
      await SettingsService.upsertSettings(businessId, [
        { 
          key: 'app.language', 
          value: languageCode, 
          data_type: 'string', 
          module_scope: 'app' 
        }
      ]);
    } catch (e) {
      console.error('Failed to save language to database:', e);
      alert(e.message || 'Failed To Save Language Preference');
    }
  };

  const handleBackgroundImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please Select A Valid Image File');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('Image size should be less than 10MB');
        return;
      }
      
      // Check if image is portrait orientation
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Validate portrait orientation (height should be greater than width)
          if (img.width >= img.height) {
            alert('❌ Only portrait images are allowed for login background.\n(Current: ' + img.width + 'x' + img.height + ')');
            // Reset the file input
            e.target.value = '';
            return;
          }
          
          // If portrait, proceed with processing
          setBackgroundImage(file);
          
          // Create high-quality preview
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas to high resolution (2x for retina displays)
          const maxWidth = 1920 * 2; // 4K resolution support
          const maxHeight = 1080 * 2;
          
          let width = img.width;
          let height = img.height;
          
          // Maintain aspect ratio while ensuring high quality
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Use high-quality image rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to high-quality JPEG (0.95 quality)
          const highQualityDataUrl = canvas.toDataURL('image/jpeg', 0.95);
          setBackgroundImagePreview(highQualityDataUrl);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveBackgroundImage = async () => {
    if (backgroundImage) {
      try {
        // Upload file to GCS via backend (per-business)
        const result = await authService.updateBusinessBackgroundImage(backgroundImage);
        
        // Cache the returned GCS URL for immediate use
        if (result?.imageUrl) {
          localStorage.setItem('loginBackgroundImage', result.imageUrl);
        }
        
        alert('Background image uploaded and saved successfully for your business!');
        setShowBackgroundImageModal(false);
      } catch (error) {
        console.error('Failed to upload background image:', error);
        alert('Failed to upload background image. Please try again.');
      }
    }
  };

  const handleRemoveBackgroundImage = async () => {
    try {
      // Remove from GCS + BusinessSettings (per-business)
      await authService.removeBusinessBackgroundImage();
      
      setBackgroundImage(null);
      setBackgroundImagePreview(null);
      alert('Background image removed for your business. Default background will be used.');
    } catch (error) {
      console.error('Failed to remove background image:', error);
      alert('Failed to remove background image. Please try again.');
    }
  };

  const onSelectPaymentTerm = async (days) => {
    setPaymentTermDays(days);
    setShowPaymentTermsModal(false);
    try {
      await SettingsService.upsertSettings(businessId, [{ key: 'vendors.default_payment_terms_days', value: days, data_type: 'number', module_scope: 'vendors' }]);
    } catch (e) {
      alert(e.message || 'Failed To Save Default Payment Terms');
    }
  };

  const onSelectDeviation = async (pct) => {
    setDeviation(pct);
    setShowDeviationModal(false);
    try {
      // Save the deviation percentage to settings
      await SettingsService.upsertSettings(businessId, [{ key: 'wastage.yield_tolerance_pct', value: pct, data_type: 'number', module_scope: 'wastage' }]);
      
      // Apply production tolerance directly as plate threshold for ALL menu items
      // Example: 10% tolerance → 10 plates for all items
      await WastageThresholdsApi.initMenuItemThresholds(pct);
      
      // Navigate to wastage thresholds page to show updated values
      if (goTo) {
        goTo('wastage-thresholds');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to update deviation setting');
    }
  };

  // Load payment settings when modal opens
  useEffect(() => {
    if (!showPaymentSettingsModal) return;
    
    const loadPaymentSettings = async () => {
      setLoadingPaymentSettings(true);
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${API_URL}/api/business/${businessId}/payment-settings`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          setPaymentSettings({
            paymentMethod: data.paymentMethod || 'razorpay',
            vpa: data.vpa || '',
            merchantUpiLink: data.merchantUpiLink,
            businessName: data.businessName || ''
          });
        } else {
          console.error('Failed to load payment settings:', data.error);
        }
      } catch (error) {
        console.error('Failed to load payment settings:', error);
        alert('Failed to load payment settings');
      } finally {
        setLoadingPaymentSettings(false);
      }
    };
    
    loadPaymentSettings();
  }, [showPaymentSettingsModal, businessId]);

  useEffect(() => {
    if (!showPaymentSettingsModal) {
      setPaymentMethodDropdownOpen(false);
      return;
    }

    const handleClickOutside = (event) => {
      if (paymentMethodDropdownOpen && !event.target.closest('.sp-payment-method-select-wrapper')) {
        setPaymentMethodDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPaymentSettingsModal, paymentMethodDropdownOpen]);

  const handleSavePaymentSettings = async () => {
    // Validate VPA if UPI is selected
    if (paymentSettings.paymentMethod === 'upi') {
      if (!paymentSettings.vpa) {
        alert('Please enter your UPI ID / VPA');
        return;
      }
      
      // Validate VPA format - must contain @ symbol
      if (!paymentSettings.vpa.includes('@')) {
        alert('Invalid UPI ID format!\n\nUPI ID must be in format: username@provider\n\nExamples:\n• 9876543210@paytm\n• merchant@ybl (PhonePe)\n• user@oksbi (SBI)\n\nPlease check your UPI app for the correct format.');
        return;
      }
    }

    setLoadingPaymentSettings(true);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/business/${businessId}/payment-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          vpa: paymentSettings.vpa,
          paymentMethod: paymentSettings.paymentMethod
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Payment settings saved successfully!');
        setPaymentSettings(prev => ({
          ...prev,
          merchantUpiLink: data.merchantUpiLink
        }));
        setShowPaymentSettingsModal(false);
      } else {
        alert(`Error: ${data.error || 'Failed to save settings'}`);
      }
    } catch (error) {
      console.error('Failed to save payment settings:', error);
      alert('Failed to save payment settings. Please try again.');
    } finally {
      setLoadingPaymentSettings(false);
    }
  };

  // After notificationSettings useState initialization, add effect to load prefs
  useEffect(() => {
    const userId = currentUser?.userId || currentUser?.user_id || currentUser?.id;
    if (!userId) return;
    (async () => {
      try {
        const prefs = await SettingsService.getNotificationPreferences(businessId, userId);
        const map = {};
        (prefs.data || []).forEach(p => { map[p.alert_type] = !!p.is_enabled; });
        // Only overwrite keys the server actually returned; anything absent
        // keeps its default-on value.
        setNotificationSettings(prev => {
          const next = { ...prev };
          Object.keys(prev).forEach(key => {
            if (key in map) next[key] = map[key];
          });
          return next;
        });
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleNotificationToggle = useCallback(async (key, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: value,
    }));
    try {
      const userId = currentUser?.userId || currentUser?.user_id || currentUser?.id;
      if (!userId) return;
      await SettingsService.updateNotificationPreferences(businessId, userId, { [key]: value });
    } catch (e) {
      // revert on failure
      setNotificationSettings(prev => ({ ...prev, [key]: !value }));
      alert(e.message || 'Failed To Update Notification Preference');
    }
  }, [currentUser, businessId]);

  // Operating hours helper functions
  const toggleDayStatus = (day) => {
    setOperatingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        isOpen: !prev[day].isOpen
      }
    }));
  };

  const enableShift = (day, shiftName) => {
    setOperatingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        shifts: {
          ...prev[day].shifts,
          [shiftName]: {
            ...prev[day].shifts[shiftName],
            enabled: true
          }
        }
      }
    }));
  };

  const disableShift = (day, shiftName) => {
    setOperatingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        shifts: {
          ...prev[day].shifts,
          [shiftName]: {
            ...prev[day].shifts[shiftName],
            enabled: false
          }
        }
      }
    }));
  };

  const updateShiftTime = (day, shiftName, field, value) => {
    setOperatingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        shifts: {
          ...prev[day].shifts,
          [shiftName]: {
            ...prev[day].shifts[shiftName],
            [field]: value
          }
        }
      }
    }));
  };

  // Load business hours from database
  useEffect(() => {
    const loadBusinessHours = async () => {
      try {
        const response = await BusinessHoursService.getBusinessHours();
        
        if (response.success && response.data && response.data.length > 0) {
          // Convert database format to UI state format
          const hoursData = response.data;
          const newOperatingHours = {};
          
          // Initialize all days
          const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
          daysOfWeek.forEach(day => {
            newOperatingHours[day] = {
              isOpen: false,
              shifts: {
                morning: { enabled: false, start: '6:00 AM', end: '12:00 PM' },
                afternoon: { enabled: false, start: '12:00 PM', end: '6:00 PM' },
                evening: { enabled: false, start: '6:00 PM', end: '12:00 AM' }
              }
            };
          });
          
          // Populate with database data
          hoursData.forEach(hour => {
            const day = hour.day_of_week;
            const shiftType = hour.shift_type.toLowerCase(); // 'Morning' -> 'morning'
            
            if (newOperatingHours[day] && newOperatingHours[day].shifts[shiftType]) {
              newOperatingHours[day].isOpen = hour.is_open;
              newOperatingHours[day].shifts[shiftType] = {
                enabled: hour.is_open,
                start: BusinessHoursService.formatTime(hour.opens_at),
                end: BusinessHoursService.formatTime(hour.closes_at)
              };
            }
          });
          
          setOperatingHours(newOperatingHours);
        }
      } catch (error) {
        console.error('❌ Failed to load business hours:', error);
        // Keep default hardcoded state if API fails
      }
    };
    
    loadBusinessHours();
  }, []);

  // Save operating hours to database
  const saveOperatingHours = async () => {
    try {
      
      // Convert UI state to database format
      const hoursArray = [];
      
      Object.entries(operatingHours).forEach(([day, dayData]) => {
        Object.entries(dayData.shifts).forEach(([shiftName, shiftData]) => {
          if (shiftData.enabled) {
            hoursArray.push({
              day_of_week: day,
              shift_type: shiftName.charAt(0).toUpperCase() + shiftName.slice(1), // 'morning' -> 'Morning'
              is_open: dayData.isOpen,
              opens_at: BusinessHoursService.parseTime(shiftData.start),
              closes_at: BusinessHoursService.parseTime(shiftData.end)
            });
          }
        });
      });
      
      
      const response = await BusinessHoursService.bulkUpdateHours(hoursArray);
      
      if (response.success) {
        
        // Broadcast event to notify Usage.js and other components
        businessHoursEventService.hoursUpdated();
        
        setShowOperatingHoursModal(false);
        alert('Operating hours saved successfully! Changes will reflect in Stock Out form immediately.');
      } else {
        throw new Error(response.error || 'Failed to save business hours');
      }
    } catch (error) {
      console.error('❌ Failed to save business hours:', error);
      alert(`Failed to save operating hours: ${error.message}`);
    }
  };

  // Load payment methods from backend when modal opens
  useEffect(() => {
    if (!showPaymentMethodsModal) return;
    (async () => {
      try {
        const res = await SettingsService.listPaymentMethods(businessId);
        const list = (res.data || []).map(pm => ({ id: String(pm.payment_method_id), label: pm.method_name, description: pm.description }));
        setPaymentMethods(list);
      } catch (e) {
        console.error('Failed to load payment methods', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentMethodsModal]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    
    const searchLower = searchQuery.toLowerCase();
    
    // Priority 1: Name starts with search term
    const nameStartsWithMatches = users.filter(user =>
      (user.name || '').toLowerCase().startsWith(searchLower)
    );
    
    // Priority 2: Email starts with search term (but name doesn't start with it)
    const emailStartsWithMatches = users.filter(user =>
      !(user.name || '').toLowerCase().startsWith(searchLower) &&
      (user.email || '').toLowerCase().startsWith(searchLower)
    );
    
    // Priority 3: Role starts with search term (but name and email don't start with it)
    const roleStartsWithMatches = users.filter(user =>
      !(user.name || '').toLowerCase().startsWith(searchLower) &&
      !(user.email || '').toLowerCase().startsWith(searchLower) &&
      (user.role || '').toLowerCase().startsWith(searchLower)
    );
    
    // Priority 4: Contains search term in name (but doesn't start with it)
    const nameContainsMatches = users.filter(user =>
      (user.name || '').toLowerCase().includes(searchLower) &&
      !(user.name || '').toLowerCase().startsWith(searchLower)
    );
    
    // Priority 5: Contains search term in email (but doesn't start with it and name doesn't match)
    const emailContainsMatches = users.filter(user =>
      !(user.name || '').toLowerCase().includes(searchLower) &&
      (user.email || '').toLowerCase().includes(searchLower) &&
      !(user.email || '').toLowerCase().startsWith(searchLower)
    );
    
    // Priority 6: Contains search term in role (but others don't match)
    const roleContainsMatches = users.filter(user =>
      !(user.name || '').toLowerCase().includes(searchLower) &&
      !(user.email || '').toLowerCase().includes(searchLower) &&
      (user.role || '').toLowerCase().includes(searchLower) &&
      !(user.role || '').toLowerCase().startsWith(searchLower)
    );
    
    return [
      ...nameStartsWithMatches,
      ...emailStartsWithMatches,
      ...roleStartsWithMatches,
      ...nameContainsMatches,
      ...emailContainsMatches,
      ...roleContainsMatches
    ];
  }, [users, searchQuery]);

  const fetchPerformanceMeter = async () => {
    const startedAt = Date.now();
    try {
      const [healthRes, dbRes] = await Promise.all([
        HealthService.getHealth().catch(() => null),
        HealthService.getDatabaseStatus().catch(() => null)
      ]);

      const latencyMs = Math.max(1, Date.now() - startedAt);
      const backendOk = Boolean(healthRes && (healthRes.status === 'ok' || healthRes.success === true));
      const databaseOk = Boolean(
        dbRes && (
          dbRes.success === true ||
          dbRes.connected === true ||
          dbRes.status === 'ok' ||
          dbRes.database?.status === 'connected' ||
          dbRes.database?.connected === true
        )
      );

      let score = 0;
      if (backendOk) score += 45;
      if (databaseOk) score += 45;
      if (latencyMs <= 200) score += 10;
      else if (latencyMs <= 500) score += 8;
      else if (latencyMs <= 1000) score += 5;

      setPerformanceMeterData({
        score: Math.min(100, score),
        backendStatus: backendOk ? 'healthy' : 'down',
        databaseStatus: databaseOk ? 'healthy' : 'down',
        latencyMs,
        checkedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to fetch performance meter:', error);
      setPerformanceMeterData((prev) => ({
        ...prev,
        checkedAt: new Date().toISOString()
      }));
    }
  };

  const handleClosingStockCorrectionToggle = async (enabled) => {
    setToggleStates((prev) => ({
      ...prev,
      [CLOSING_STOCK_CORRECTION_TOGGLE_ID]: enabled,
    }));

    try {
      localStorage.setItem(`closingStockCorrectionEnabled_${businessId}`, enabled ? 'true' : 'false');
      await SettingsService.upsertSettings(businessId, [{
        key: 'inventory.closing_stock_correction_enabled',
        value: enabled,
        data_type: 'boolean',
        module_scope: 'inventory',
        description: 'When enabled, Overview displays zero quantity and supports closing-stock correction workflow.',
      }]);
    } catch (error) {
      console.error('Failed to save closing stock correction toggle:', error);
      setToggleStates((prev) => ({
        ...prev,
        [CLOSING_STOCK_CORRECTION_TOGGLE_ID]: !enabled,
      }));
      localStorage.setItem(`closingStockCorrectionEnabled_${businessId}`, !enabled ? 'true' : 'false');
      alert('Failed to save closing stock toggle. Please try again.');
      return;
    }
  };

  const handleLockClosingStockToggle = async (enabled) => {
    // enabled is true if switch is ON (user wants to start closing stock counting, which locks Stock-In).
    // enabled is false if switch is OFF (user wants to finish closing stock counting, which unlocks Stock-In).
    setToggleStates((prev) => ({
      ...prev,
      'closing-stock-locked': enabled,
    }));

    try {
      await http.post('/closing-stock/toggle-lock', { businessId, enabled: enabled });
      if (enabled) {
        setAlertDialog({
          open: true,
          title: 'Closing Stock Active',
          message: 'Closing stock is now active. Stock-In is locked.',
          tone: 'info'
        });
      } else {
        setAlertDialog({
          open: true,
          title: 'Closing Stock Inactive',
          message: 'Closing stock is now inactive. Stock-In is allowed.',
          tone: 'info'
        });
      }
    } catch (error) {
      console.error('Failed to toggle closing stock lock status:', error);
      setToggleStates((prev) => ({
        ...prev,
        'closing-stock-locked': !enabled,
      }));
      alert('Failed to update closing stock lock status. Please try again.');
    }
  };

  // Paragraph break inside a confirm(). Kept as a constant so the dialog text below
  // reads as prose instead of being broken up by escape sequences.
  const BREAK = String.fromCharCode(10, 10);

  /**
   * C11. This one WRITES. It creates real Stock-In records, real batches, and moves
   * current_stock — and it sits one row away from "Estimate Cost", which changes nothing
   * but a report. The names were close enough to confuse, and a mistaken press here is not
   * undone by flipping a switch back: it is undone by finding and reverting the entries.
   *
   * So the confirmation spells out what changes rather than asking "are you sure": the
   * question an owner needs answered is "what will this do to my stock", and the answer
   * has to be in the dialog, not in the subtitle they already skimmed past.
   */
  const handleNegativeStockFixToggle = async (enabled) => {
    if (!enabled) {
      setToggleStates((prev) => ({
        ...prev,
        [NEGATIVE_STOCK_FIX_TOGGLE_ID]: false,
      }));
      return;
    }

    const proceed = await showAppConfirm(
      'This CHANGES YOUR STOCK. It creates real Stock-In entries for every item currently '
      + 'below zero, priced from that item’s previous purchase, and raises its stock to zero. '
      + 'The entries are permanent records — undoing this means finding and reverting them, '
      + 'not switching anything off.' + BREAK
      + 'If you only wanted reports to stop showing dashes, cancel this and use '
      + '“Show Last Known Price Instead of Dashes” instead — that one changes the report only.' + BREAK
      + 'Create the stock entries now?'
    );
    if (!proceed) {
      setToggleStates((prev) => ({
        ...prev,
        [NEGATIVE_STOCK_FIX_TOGGLE_ID]: false,
      }));
      return;
    }

    if (!businessId) {
      showAppAlert('Missing business ID. Please log in again.');
      setToggleStates((prev) => ({
        ...prev,
        [NEGATIVE_STOCK_FIX_TOGGLE_ID]: false,
      }));
      return;
    }

    if (isNegativeStockFixRunning) {
      return;
    }

    setToggleStates((prev) => ({
      ...prev,
      [NEGATIVE_STOCK_FIX_TOGGLE_ID]: true,
    }));
    setIsNegativeStockFixRunning(true);

    try {
      const result = await SettingsService.fixNegativeStockWithLatestPrices(businessId);
      if (!result || result.success !== true) {
        throw new Error(result?.error || 'Negative stock auto-fix failed');
      }

      const appliedCount = Number(result.appliedCount || 0);
      const skippedCount = Number(result.skippedCount || 0);
      const failedCount = Number(result.failedCount || 0);
      const skippedItems = Array.isArray(result?.data?.skipped) ? result.data.skipped : [];
      const skippedNames = skippedItems
        .map((item) => String(item?.item_name || '').trim())
        .filter(Boolean)
        .slice(0, 3);

      let message = `Negative stock auto-fix complete. Fixed ${appliedCount} item(s).`;
      if (skippedCount > 0) {
        const sample = skippedNames.length ? ` (e.g. ${skippedNames.join(', ')}${skippedCount > skippedNames.length ? '...' : ''})` : '';
        message += ` Skipped ${skippedCount} item(s) without latest price${sample}.`;
      }
      if (failedCount > 0) {
        message += ` Failed to fix ${failedCount} item(s).`;
      }

      showAppAlert(message);
    } catch (error) {
      console.error('Failed to auto-fix negative stock:', error);
      showAppAlert(`Negative stock auto-fix failed. ${error?.message || 'Please try again.'}`);
    } finally {
      setIsNegativeStockFixRunning(false);
      setToggleStates((prev) => ({
        ...prev,
        [NEGATIVE_STOCK_FIX_TOGGLE_ID]: false,
      }));
    }
  };

  /**
   * Negative-stock price fallback.
   *
   * Deliberately NOT mirrored into localStorage like the toggles above: report values
   * are decided server-side from this setting, and a local mirror would invite a
   * screen to make that call itself — which is how two surfaces end up disagreeing
   * about the same day's profit.
   *
   * Turning it ON is confirmed first, because it changes what published financial
   * numbers mean. Turning it OFF needs no confirmation: that only ever returns
   * reports to the stricter, dash-showing behaviour.
   */
  const handleNegativeStockPriceFallbackToggle = async (enabled) => {
    if (enabled) {
      const proceed = await showAppConfirm(
        'Reports will show estimated costs for ingredients you do not have stock for. '
        + 'These are estimates based on old purchase prices, not what you actually paid. '
        + 'Estimated figures are marked with ~ on screen and in exports, and cannot be '
        + 'used to close a month. Continue?'
      );
      if (!proceed) {
        setToggleStates((prev) => ({
          ...prev,
          [NEGATIVE_STOCK_PRICE_FALLBACK_TOGGLE_ID]: false,
        }));
        return;
      }
    }

    setToggleStates((prev) => ({
      ...prev,
      [NEGATIVE_STOCK_PRICE_FALLBACK_TOGGLE_ID]: enabled,
    }));

    try {
      await SettingsService.upsertSettings(businessId, [{
        key: 'reports.negative_stock_price_fallback_enabled',
        value: enabled,
        data_type: 'boolean',
        module_scope: 'reports',
        description: 'When enabled, reports price negative-stock ingredients at the last known purchase price instead of showing a dash. Estimated figures are labelled.',
      }]);
      showAppAlert(enabled
        ? 'Estimated costs are ON. Figures marked with ~ include ingredients priced from an old purchase price.'
        : 'Estimated costs are OFF. Reports show a dash where a cost cannot be established.');
    } catch (error) {
      console.error('Failed to save negative stock price fallback toggle:', error);
      setToggleStates((prev) => ({
        ...prev,
        [NEGATIVE_STOCK_PRICE_FALLBACK_TOGGLE_ID]: !enabled,
      }));
      showAppAlert('Failed to save setting. Please try again.');
    }
  };

  /**
   * B8 — the estimate age limit.
   *
   * 90 days suits a kitchen that buys weekly. It is wrong for one that buys whole spices
   * or dried goods twice a year: their genuinely valid last price is refused as stale, the
   * dash stays, and the resulting complaint is indistinguishable from a bug. Widening the
   * window has to be possible, and it has to be a deliberate choice rather than something
   * done for the owner over a support call.
   *
   * Owner/Admin only, and not because it is dangerous — it is not, it can only make the
   * report MORE willing to publish an old price — but because that willingness is a
   * financial-reporting policy, and policy belongs to whoever answers for the numbers.
   */
  const handleNegativeStockPriceMaxAgeEdit = async () => {
    const role = String(userRole || '').toLowerCase();
    if (!['owner', 'admin'].includes(role)) {
      showAppAlert('Only an Owner or Admin can change the estimate age limit.');
      return;
    }
    const raw = prompt(
      'Refuse a purchase price older than how many days when estimating?\n\n'
      + 'Lower is stricter (more dashes, fewer estimates). 0 removes the limit entirely, '
      + 'which lets a price of any age be published as an estimate.',
      String(negativeStockPriceMaxAge)
    );
    if (raw === null) return;
    const days = Number(String(raw).trim());
    if (!Number.isFinite(days) || days < 0 || !Number.isInteger(days)) {
      showAppAlert('Enter a whole number of days, 0 or more.');
      return;
    }
    if (days > 3650) {
      showAppAlert('That is over ten years. Enter 0 if you genuinely want no age limit.');
      return;
    }

    const previous = negativeStockPriceMaxAge;
    setNegativeStockPriceMaxAge(days);
    try {
      await SettingsService.upsertSettings(businessId, [{
        key: 'reports.negative_stock_price_fallback_max_age_days',
        value: days,
        data_type: 'number',
        module_scope: 'reports',
        description: 'Maximum age, in days, of a purchase price used to estimate the cost of a negative-stock ingredient. 0 disables the check.',
      }]);
      showAppAlert(days === 0
        ? 'Estimate age limit removed. A purchase price of any age can now be used as an estimate.'
        : `Estimate age limit set to ${days} day(s). Older prices will keep showing a dash.`);
    } catch (error) {
      console.error('Failed to save estimate age limit:', error);
      // Put the displayed value back: a screen showing a limit the server never accepted
      // is exactly the "I changed it and nothing happened" report this row exists to avoid.
      setNegativeStockPriceMaxAge(previous);
      showAppAlert('Failed to save the age limit. Please try again.');
    }
  };

  const handleVendorAutofillPromptToggle = async (enabled) => {
    setToggleStates((prev) => ({
      ...prev,
      [VENDOR_AUTOFILL_PROMPT_TOGGLE_ID]: enabled,
    }));

    try {
      localStorage.setItem(`vendorAutofillPromptEnabled_${businessId}`, enabled ? 'true' : 'false');
      await SettingsService.upsertSettings(businessId, [{
        key: 'inventory.vendor_autofill_prompt_enabled',
        value: enabled,
        data_type: 'boolean',
        module_scope: 'inventory',
        description: 'When enabled, selecting a vendor in manual Stock-In shows a popup to autofill items from the last order by that vendor.',
      }]);
    } catch (error) {
      console.error('Failed to save vendor autofill prompt toggle:', error);
      setToggleStates((prev) => ({
        ...prev,
        [VENDOR_AUTOFILL_PROMPT_TOGGLE_ID]: !enabled,
      }));
      localStorage.setItem(`vendorAutofillPromptEnabled_${businessId}`, !enabled ? 'true' : 'false');
      alert('Failed to save setting. Please try again.');
    }
  };

  const handleTaxForStockInToggle = async (enabled) => {
    setToggleStates((prev) => ({
      ...prev,
      'tax-for-stock-in': enabled,
    }));

    try {
      localStorage.setItem(`taxForStockInEnabled_${businessId}`, enabled ? 'true' : 'false');
      await SettingsService.upsertSettings(businessId, [{
        key: 'sales.tax_for_stock_in_enabled',
        value: enabled,
        data_type: 'boolean',
        module_scope: 'sales',
        description: 'When enabled, manual stock entry displays per-item tax inputs instead of a single header-level tax card.',
      }]);
    } catch (error) {
      console.error('Failed to save tax for stock in toggle:', error);
      setToggleStates((prev) => ({
        ...prev,
        'tax-for-stock-in': !enabled,
      }));
      localStorage.setItem(`taxForStockInEnabled_${businessId}`, !enabled ? 'true' : 'false');
      alert('Failed to save setting. Please try again.');
    }
  };

  const settingSections = useMemo(() => [
    {
      id: 'account',
      title: t('settings.account_management'),
      icon: <FaUser />,
      expanded: expandedSections.includes('account'),
      items: [
        {
          id: 'edit-profile',
          title: t('settings.my_profile'),
          subtitle: currentUser 
            ? `${currentUser.name || ''} | ${currentUser.email || ''} ${currentUser.phone || currentUser.phoneNumber ? `| ${currentUser.phone || currentUser.phoneNumber}` : ''}${currentUser.profilePicture ? ' • Profile photo set' : ''}`
            : t('settings.view_edit_profile'),
          type: 'navigation',
          action: () => {
            // Reset form with current user data when opening modal
            if (currentUser) {
              setProfileForm({
                name: currentUser.fullName || currentUser.name || '',
                email: currentUser.email || '',
                phone: currentUser.phone || currentUser.phoneNumber || ''
              });
              // Set current profile picture if exists
              if (currentUser.profilePicture) {
                setProfilePicturePreview(currentUser.profilePicture);
              }
            }
            setShowEditProfileModal(true);
          },
        },
        {
          id: 'change-password',
          title: t('settings.change_password'),
          subtitle: t('settings.update_account_security'),
          type: 'navigation',
          action: () => { setChangePwdStep(1); setShowChangePasswordModal(true); },
        },
        {
          id: 'language',
          title: t('settings.app_language'),
          subtitle: selectedLanguage,
          type: 'button',
          action: () => setShowLanguageModal(true),
        },
        {
          id: 'background-image',
          title: 'Login Background',
          subtitle: backgroundImagePreview ? 'Custom background set' : 'Using default background',
          type: 'navigation',
          action: () => setShowBackgroundImageModal(true),
        },
        {
          id: 'logout',
          title: t('settings.sign_out'),
          subtitle: t('settings.logout_account'),
          type: 'navigation',
          action: async () => {
            try {
              // Use AuthContext logout function
              await logout();
              // Navigate to login screen
              goTo('login');
            } catch (error) {
              console.error('Error during signout:', error);
              // Even if logout fails, still navigate to login
              goTo('login');
            }
          },
        },
      ],
    },
    {
      id: 'users',
      title: t('sidebar.user_access_management'),
      icon: <FaUsers />,
      expanded: expandedSections.includes('users'),
      items: [
        {
          id: 'invite-user',
          title: t('sidebar.invite_new_user'),
          subtitle: t('sidebar.send_invitation'),
          type: 'navigation',
          action: () => setShowInviteUserModal(true),
        },
        {
          id: 'manage-users',
          title: t('sidebar.manage_users'),
          subtitle: `${users.length} ${t('sidebar.total_users')} (${users.filter(u => u.status === 'Active').length} ${t('sidebar.active')})`,
          type: 'navigation',
          action: () => setShowUserManagementModal(true),
        },
        {
          id: 'roles-permissions',
          title: t('sidebar.roles_permissions'),
          subtitle: `${roles.length} ${t('sidebar.roles_configured')}`,
          type: 'navigation',
          action: () => {
            setSelectedRole('Owner/Admin');
            setShowRolePermissionsModal(true);
          },
        },
        ...(userRole?.toLowerCase().includes('owner') || userRole?.toLowerCase().includes('admin') ? [{
          id: 'audit-logs',
          title: ' Audit Logs (Owner Only)',
          subtitle: 'View all permission changes and user actions',
          type: 'navigation',
          action: () => setShowAuditLogModal(true),
        }] : []),
      ],
    },
    {
      id: 'business',
      title: t('sidebar.business_location_management'),
      icon: <FaBuilding />,
      expanded: expandedSections.includes('business'),
      items: [
        {
          id: 'business-profile',
          title: 'Restaurant Profile',
          subtitle: 'Business name, address, contact details',
          type: 'navigation',
          action: () => setShowRestaurantProfileModal(true),
        },
        {
          id: 'manage-locations',
          title: 'Manage Locations',
          subtitle: `${locations.length} active branches`,
          type: 'navigation',
          action: () => setShowManageLocationsModal(true),
        },
        {
          id: 'operating-hours',
          title: 'Operating Hours',
          subtitle: 'Configure business shifts',
          type: 'navigation',
          action: () => setShowOperatingHoursModal(true),
        },
        {
          id: 'payment-settings',
          title: 'Payment Settings',
          subtitle: 'Configure UPI or Razorpay payment',
          type: 'navigation',
          action: () => setShowPaymentSettingsModal(true),
        },
      ],
    },
    {
      id: 'inventory',
      title: t('settings.inventory_management'),
      icon: <FaBox />,
      expanded: expandedSections.includes('inventory'),
      items: [
        {
          id: CLOSING_STOCK_CORRECTION_TOGGLE_ID,
          title: 'Closing Stock',
          subtitle: 'Enter physical stock count, download reports',
          type: 'navigation',
          action: () => goTo('closing-stock'),
        },
        {
          id: 'revert-closing-stock',
          title: 'Revert Closing Stock',
          subtitle: 'Revert or undo recently submitted closing stock changes',
          type: 'navigation',
          action: openRevertClosingStock,
        },
        {
          id: 'closing-stock-locked',
          title: 'Lock Closing Stock',
          subtitle: 'When ON, Stock-In is locked (allows physical stock count entry)',
          type: 'toggle',
          onToggle: handleLockClosingStockToggle,
        },
        {
          id: 'closing-stock-override-pin',
          title: 'Override PIN',
          subtitle: 'Set 4-digit PIN for guard rail overrides',
          type: 'navigation',
          action: () => {
            const pin = prompt('Enter new 4-digit override PIN:');
            if (pin && /^\d{4}$/.test(pin.trim())) {
              const bId = UnitMappingService?.getBusinessId?.() || localStorage.getItem('businessId');
              http.post('/closing-stock/set-pin', { businessId: bId, pin: pin.trim() })
                .then(() => alert('Override PIN set successfully!'))
                .catch((e) => alert('Failed to set PIN: ' + (e.message || e)));
            } else if (pin !== null) {
              alert('PIN must be exactly 4 digits.');
            }
          },
        },
        {
          // C11. ORDER IS THE POINT. The safe, reversible, report-only setting comes FIRST,
          // because it is what almost everyone reaching for "my reports show dashes"
          // actually wants. The one that writes permanent stock records comes second, named
          // for what it does to the data rather than for the symptom it treats.
          //
          // These two were adjacent and near-identically named ("Negative Stock Auto Fix" /
          // "Estimate Cost for Negative-Stock Items"), which made most complaints about the
          // destructive one get filed against the harmless one.
          id: NEGATIVE_STOCK_PRICE_FALLBACK_TOGGLE_ID,
          title: 'Show Last Known Price Instead of Dashes',
          subtitle: 'Reports only — your stock is not changed. When ON, an ingredient with no stock and no confirmed closing-stock price is costed at its last purchase price instead of showing a dash. Estimates are marked with ~ and cannot sign off a month.',
          type: 'toggle',
          onToggle: handleNegativeStockPriceFallbackToggle,
        },
        {
          // B8. The staleness window. 90 days is right for a kitchen that buys weekly and
          // wrong for one that buys whole spices twice a year — whose prices are refused as
          // stale and who then files a dash complaint that looks like a bug. Owner-visible
          // so widening the window is a deliberate, attributable choice rather than a
          // support conversation.
          id: NEGATIVE_STOCK_PRICE_MAX_AGE_ID,
          title: 'Estimate Age Limit',
          subtitle: `Refuse a purchase price older than this when estimating. Currently ${negativeStockPriceMaxAge} day(s); 0 means no limit.`,
          type: 'navigation',
          action: handleNegativeStockPriceMaxAgeEdit,
          hidden: !toggleStates[NEGATIVE_STOCK_PRICE_FALLBACK_TOGGLE_ID],
        },
        {
          id: NEGATIVE_STOCK_FIX_TOGGLE_ID,
          title: 'Rebuild Negative Stock — writes stock records',
          subtitle: isNegativeStockFixRunning
            ? 'Running: creating Stock-In entries to raise negative items to zero'
            : 'CHANGES YOUR STOCK. Creates permanent Stock-In entries for every item below zero, priced from its previous purchase. Undo means reverting those entries, not switching this off.',
          type: 'toggle',
          onToggle: handleNegativeStockFixToggle,
        },
        {
          id: VENDOR_AUTOFILL_PROMPT_TOGGLE_ID,
          title: 'Vendor Previous Order Autofill',
          subtitle: 'When ON, selecting a vendor in manual Stock-In shows a popup to prefill items from their last order',
          type: 'toggle',
          onToggle: handleVendorAutofillPromptToggle,
        },
        {
          id: 'units-measure',
          title: t('settings.units_of_measure'),
          subtitle: t('settings.manage_units'),
          type: 'navigation',
          action: () => (onNavigateToUnits ? onNavigateToUnits() : (goTo && goTo('itemmap'))),
        },
        {
          id: 'piece-guard-answers',
          title: 'Piece Guard — Pieces or Packets',
          subtitle: 'See whether each pcs ingredient was answered as pieces or packets, and correct a wrong answer and its purchases',
          type: 'navigation',
          action: () => setShowPieceGuardSettings(true),
        },
        {
          id: 'last-purchases',
          title: 'Last 5 purchases',
          subtitle: 'Each ingredient\'s five most recent purchases — date, vendor, quantity and price',
          type: 'navigation',
          action: () => setShowLastPurchases(true),
        },
        {
          id: 'cx-price-history',
          title: 'Price history',
          subtitle: 'What each ingredient usually costs, its last five purchases, and the entries that need a look',
          type: 'navigation',
          action: () => setShowPriceHistory(true),
          hidden: !cxHistoryOn,
        },
        {
          id: 'inventory-categories',
          title: t('settings.inventory_categories'),
          subtitle: t('settings.category_types'),
          type: 'navigation',
          action: () => setShowInventoryCategoriesModal(true),
        },
        {
          id: 'sales-name-check',
          title: 'Sales Name Check',
          subtitle: 'Match your POS dish names to the menu',
          type: 'navigation',
          action: () => (onOpenFromSettings
            ? onOpenFromSettings('sales-name-check')
            : (goTo && goTo('sales-name-check'))),
          hidden: !sncVisible,
        },
        {
          id: 'ingredient-reconciliation',
          title: 'Ingredient Reconciliation',
          subtitle: 'Review chef-entered ingredients, rename, merge, and adjust units',
          type: 'navigation',
          // Opened with a way back: Back on Reconciliation returns here (spec §17 Back bug).
          action: () => (onOpenFromSettings
            ? onOpenFromSettings('ingredient-reconciliation')
            : (goTo && goTo('ingredient-reconciliation'))),
        },
        {
          id: 'bill-memory',
          title: 'Bill Memory',
          subtitle: 'Names the app has learned from your bills',
          type: 'navigation',
          action: () => (onOpenFromSettings
            ? onOpenFromSettings('bill-memory')
            : (goTo && goTo('bill-memory'))),
          hidden: !bmVisible,
        },
        {
          id: 'portion-multipliers',
          title: 'Portion Multipliers',
          subtitle: 'Manage recipe variant scaling factors',
          type: 'navigation',
          action: () => setShowPortionMultipliersModal(true),
        },
        {
          id: 'customization-rules',
          title: 'Customization Rules',
          subtitle: 'Configure modifier scalars for accurate inventory tracking',
          type: 'navigation',
          action: () => setShowCustomizationRulesModal(true),
        },
        {
          id: 'item-mapping-log',
          title: 'Item Mapping Logs',
          subtitle: 'Every ingredient-mapping action — recipe, user, date and time',
          type: 'navigation',
          action: () => setShowItemMappingLogModal(true),
        },
        {
          id: 'structural-action-log',
          title: 'Structural Action Log',
          subtitle: 'Revert inventory structural modifications (Rename, Unit Change, Merge)',
          type: 'navigation',
          action: () => goTo && goTo('structural-action-log'),
        },
      ],
    },
    {
      id: 'sales',
      subtitle: `${users.length} total users (${users.filter(u => u.status === 'Active').length} active)`,
      title: t('settings.sales_menu'),
      icon: <FaShoppingCart />,
      expanded: expandedSections.includes('sales'),
      items: [
        {
          id: 'menu-categories',
          title: t('settings.menu_categories'),
          subtitle: t('settings.menu_types'),
          type: 'navigation',
          action: () => setShowMenuCategoriesModal(true),
        },
        {
          id: 'menu-organization',
          title: 'Menu Organization',
          subtitle: 'Group menu categories into master cuisines',
          type: 'navigation',
          action: () => setShowMenuOrganizationModal(true),
        },
        {
          id: 'tax-rates',
          title: t('settings.tax_rates'),
          subtitle: t('settings.tax_config'),
          type: 'navigation',
          action: () => setShowTaxRatesModal(true),
        },
        {
          id: 'pricing-control',
          title: t('settings.localized_pricing'),
          subtitle: t('settings.branch_pricing'),
          type: 'navigation',
          action: () => setShowLocalizedPricingModal(true),
        },
        {
          id: 'tax-for-stock-in',
          title: 'Tax for Stock In',
          subtitle: 'Configure tax rate for manual stock entry per item',
          type: 'toggle',
          onToggle: handleTaxForStockInToggle,
        },
      ],
    },
    {
      id: 'vendors',
      title: t('settings.vendor_procurement'),
      icon: <FaTruck />,
      expanded: expandedSections.includes('vendors'),
      items: [
        {
          id: 'manage-vendors',
          title: t('settings.manage_vendors'),
          subtitle: `12 ${t('settings.active_suppliers')}`,
          type: 'navigation',
          action: () => onNavigateToVendors ? onNavigateToVendors() : goTo && goTo('vendors'),
        },
        {
          id: 'payment-terms',
          title: t('settings.payment_terms'),
          subtitle: `${t('settings.net_days')} ${paymentTermDays}`,
          type: 'navigation',
          action: () => setShowPaymentTermsModal(true),
        },
      ],
    },
    {
      id: 'wastage',
      title: t('settings.wastage_production'),
      icon: <FaTrash />,
      expanded: expandedSections.includes('wastage'),
      items: [
        {
          id: 'wastage-reasons',
          title: t('settings.wastage_reasons'),
          subtitle: t('settings.wastage_types'),
          type: 'navigation',
          action: () => setShowWastageReasonsModal(true),
        },
        {
          id: 'yield-tolerances',
          title: t('settings.yield_tolerances'),
          subtitle: `${t('settings.deviation_alert')} ${deviation}%`,
          type: 'navigation',
          action: () => {
            setShowDeviationModal(true);
          },
        },
      ],
    },
    {
      id: 'financials',
      title: t('settings.financials_profitability'),
      icon: <FaChartLine />,
      expanded: expandedSections.includes('financials'),
      items: [
        {
          id: 'net-profit-analyzer',
          title: t('settings.net_profit_analyzer'),
          subtitle: npHeadline.analyzer || (npHeadline.loading ? 'Loading this month…' : 'Fixed expenses, monthly bills and the month-end close'),
          type: 'navigation',
          action: () => (onOpenFromSettings ? onOpenFromSettings('net-profit') : goTo('net-profit')),
        },
        {
          id: 'net-profit-payroll',
          title: 'Payroll — staff salaries',
          subtitle: npHeadline.payroll || (npHeadline.loading ? 'Loading staff…' : 'Workers, salaries, advances, payments and dues'),
          type: 'navigation',
          action: () => (onOpenFromSettings ? onOpenFromSettings('net-profit', { route: 'staff' }) : goTo('net-profit')),
        },
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <FaBell />,
      expanded: expandedSections.includes('notifications'),
      items: [
        {
          id: 'emailDailyPulse',
          title: 'Daily Pulse email',
          subtitle: 'Sales, purchases and gross profit, emailed when TSR is submitted',
          type: 'toggle',
          checked: notificationSettings.emailDailyPulse,
          onToggle: (next) => handleNotificationToggle('emailDailyPulse', next),
        },
        {
          id: 'emailRadarAlerts',
          title: 'Radar alert emails',
          subtitle: 'Alerts when wastage, discounts, vendor prices or sales deviate from your 7-day average',
          type: 'toggle',
          checked: notificationSettings.emailRadarAlerts,
          onToggle: (next) => handleNotificationToggle('emailRadarAlerts', next),
        },
        {
          id: 'emailMissedTsrRequired',
          title: 'Missed TSR reminder emails — always on',
          subtitle: 'A missed sales report blocks closing stock and month-end close, so this alert cannot be turned off',
          type: 'info',
        },
        {
          id: 'emailMonthlyAudit',
          title: 'Monthly Health Audit email',
          subtitle: 'Month-end audit PDF, sent on the 1st',
          type: 'toggle',
          checked: notificationSettings.emailMonthlyAudit,
          onToggle: (next) => handleNotificationToggle('emailMonthlyAudit', next),
        },
        {
          id: 'endOfDayReports',
          title: 'End-of-day report (in-app)',
          subtitle: 'In-app notification when the day’s report is ready',
          type: 'toggle',
          checked: notificationSettings.endOfDayReports,
          onToggle: (next) => handleNotificationToggle('endOfDayReports', next),
        },
        {
          id: 'dailyReportReminders',
          title: 'Daily report reminders (in-app)',
          subtitle: 'In-app reminder when a daily report is missing',
          type: 'toggle',
          checked: notificationSettings.dailyReportReminders,
          onToggle: (next) => handleNotificationToggle('dailyReportReminders', next),
        },
        {
          id: 'monthlyReports',
          title: 'Monthly reports (in-app)',
          subtitle: 'In-app notification when the monthly report is ready',
          type: 'toggle',
          checked: notificationSettings.monthlyReports,
          onToggle: (next) => handleNotificationToggle('monthlyReports', next),
        },
        {
          id: 'performanceAlerts',
          title: 'Performance alerts (in-app)',
          subtitle: 'In-app alerts for unusual sales volume and wastage trends',
          type: 'toggle',
          checked: notificationSettings.performanceAlerts,
          onToggle: (next) => handleNotificationToggle('performanceAlerts', next),
        },
      ],
    },
    {
      id: 'support',
      title: t('settings.app_info_support'),
      icon: <FaQuestionCircle />,
      expanded: expandedSections.includes('support'),
      items: [
        {
          id: 'performance-meter',
          title: 'Performance Meter',
          subtitle: 'Database and backend responsiveness score',
          type: 'navigation',
          action: () => {
            setShowPerformanceMeterModal(true);
            fetchPerformanceMeter();
          },
        },
        {
          id: 'help-support',
          title: t('settings.help_support'),
          subtitle: t('settings.help_resources'),
          type: 'navigation',
          action: () => alert('Access Help Resources'),
        },
        {
          id: 'about',
          title: t('settings.about_app'),
          subtitle: t('settings.app_version'),
          type: 'navigation',
          action: () => alert('App Version And Legal Information'),
        },
      ],
    },
  ], [currentUser, expandedSections, users, roles, locations, selectedLanguage, paymentTermDays, deviation, toggleStates, notificationSettings, handleNotificationToggle, isNegativeStockFixRunning, t, logout, goTo, onNavigateToUnits, onNavigateToVendors, setShowEditProfileModal, setShowChangePasswordModal, setShowLanguageModal, setShowInviteUserModal, setShowUserManagementModal, setShowRolePermissionsModal, setShowRestaurantProfileModal, setShowManageLocationsModal, setShowOperatingHoursModal, setShowInventoryCategoriesModal, setShowMenuCategoriesModal, setShowMenuOrganizationModal, setShowTaxRatesModal, setShowPaymentMethodsModal, setShowLocalizedPricingModal, setShowPaymentTermsModal, setShowWastageReasonsModal, setShowDeviationModal, setChangePwdStep, setProfileForm, handleClosingStockCorrectionToggle, handleNegativeStockFixToggle, handleTaxForStockInToggle, handleLockClosingStockToggle, cxHistoryOn, sncVisible, bmVisible, onOpenFromSettings, npHeadline]);

  // Filtered settings sections based on search query
  const filteredSettingSections = useMemo(() => {
    const query = settingsSearchQuery.trim().toLowerCase();
    if (!query) return settingSections;

    return settingSections
      .map(section => {
        const sectionTitleMatch = section.title?.toLowerCase().includes(query);
        const matchingItems = section.items.filter(item =>
          item.title?.toLowerCase().includes(query)
        );

        // Prioritize item matches — show only matching items
        if (matchingItems.length > 0) {
          return { ...section, expanded: true, items: matchingItems };
        }
        // If only section title matches (no items match), show all items
        if (sectionTitleMatch) {
          return { ...section, expanded: true };
        }
        return null;
      })
      .filter(Boolean);
  }, [settingSections, settingsSearchQuery]);

  // Load actual permissions from backend for selected role
  const loadRolePermissions = async (roleName) => {
    if (!roleName) return;
    
    try {
      setRolePermissionsLoading(true);
      
      const roleObj = allRoles.find(r => r.role_name === roleName);
      if (!roleObj) {
        console.error('Role not found:', roleName);
        return;
      }

      // Load all available permissions
      const allPermsRes = await RoleService.getAllPermissions();
      const allPerms = allPermsRes.data || [];
      setAllAvailablePermissions(allPerms);

      // Load role's current permissions with access_status
      const res = await RoleService.getRolePermissions(roleObj.role_id);
      const permissionsList = res.data || [];
      
      // Store actual permissions for this role
      setActualPermissions(prev => ({
        ...prev,
        [roleName]: permissionsList
      }));

      // Set enabled permission IDs (only those with access_status = TRUE)
      const enabledIds = permissionsList
        .filter(p => p.access_status === true)
        .map(p => p.permission_id);
      setEnabledPermissionIds(enabledIds);
      

      // Update toggle states based on backend permissions
      const newStates = {};
      permissionsList.forEach(perm => {
        // Try to map backend permission to UI permission
        Object.entries(permissions).forEach(([category, perms]) => {
          perms.forEach(uiPerm => {
            const key = `${roleName}-${category}-${uiPerm}`;
            // Match if permission name is similar (basic fuzzy match)
            if (perm.permission_name.toLowerCase().includes(uiPerm.toLowerCase().split(' ')[0]) ||
                uiPerm.toLowerCase().includes(perm.permission_name.toLowerCase().replace('_', ' '))) {
              newStates[key] = true;
            }
          });
        });
      });

      setRolePermissionStates(prev => ({ ...prev, ...newStates }));
    } catch (err) {
      console.error('Failed to load role permissions:', err);
    } finally {
      setRolePermissionsLoading(false);
    }
  };

  // Load permissions when role is selected
  useEffect(() => {
    if (selectedRole && showRolePermissionsModal) {
      loadRolePermissions(selectedRole);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, showRolePermissionsModal]);

  const togglePermission = async (permissionId) => {
    if (!selectedRole) return;
    
    const roleObj = allRoles.find(r => r.role_name === selectedRole);
    if (!roleObj) return;
    
    const isCurrentlyEnabled = enabledPermissionIds.includes(permissionId);
    const newAccessStatus = !isCurrentlyEnabled;
    
    try {
      // Call the toggle API immediately for instant feedback
      await RoleService.togglePermission(roleObj.role_id, permissionId, newAccessStatus);
      
      // Update local state - use Set to prevent duplicates
      setEnabledPermissionIds(prev => {
        if (isCurrentlyEnabled) {
          return prev.filter(id => id !== permissionId);
        } else {
          // Prevent duplicate IDs (can happen with rapid toggling)
          if (prev.includes(permissionId)) return prev;
          return [...prev, permissionId];
        }
      });
      
    } catch (err) {
      console.error('Failed to toggle permission:', err);
      alert(err.message || 'Failed to toggle permission');
    }
  };

  const saveRolePermissions = async () => {
    if (!selectedRole) return;
    
    try {
      setPermissionsSaving(true);
      const roleObj = allRoles.find(r => r.role_name === selectedRole);
      if (!roleObj) {
        alert('Role not found');
        return;
      }

      // Deduplicate permission IDs before sending to backend
      const uniquePermissionIds = [...new Set(enabledPermissionIds)];
      await RoleService.updateRolePermissions(roleObj.role_id, uniquePermissionIds);
      
      // Reload permissions to reflect changes
      await loadRolePermissions(selectedRole);
      
      alert('Permissions updated successfully');
    } catch (err) {
      console.error('Failed to save permissions:', err);
      alert(err.message || 'Failed to save permissions');
    } finally {
      setPermissionsSaving(false);
    }
  };

  const renderSettingItem = (item) => {
    switch (item.type) {
      case 'toggle':
        return (
          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">{item.title}</div>
              {item.subtitle && (
                <div className="setting-subtitle">{item.subtitle}</div>
              )}
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={typeof item.checked === 'boolean' ? item.checked : !!toggleStates[item.id]}
                onChange={() => {
                  const next = typeof item.checked === 'boolean'
                    ? !item.checked
                    : !toggleStates[item.id];
                  if (typeof item.onToggle === 'function') {
                    item.onToggle(next);
                    return;
                  }
                  handleToggle(item.id);
                }}
              />
              <span className="slider round"></span>
            </label>
          </div>
        );

      // Read-only row: states a setting that exists but is deliberately not
      // user-changeable, so the absence of a toggle reads as intentional.
      case 'info':
        return (
          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">{item.title}</div>
              {item.subtitle && (
                <div className="setting-subtitle">{item.subtitle}</div>
              )}
            </div>
          </div>
        );

      case 'navigation':
      case 'button':
        return (
          <div 
            className="setting-item clickable"
            onClick={item.action}
          >
            <div className="setting-info">
              <div className="setting-title">{item.title}</div>
              {item.subtitle && (
                <div className="setting-subtitle">{item.subtitle}</div>
              )}
            </div>
            <FaChevronRight className="chevron-icon" />
          </div>
        );

      default:
        return null;
    }
  };

  const renderUserRow = (user) => (
    <div key={user.id} className="user-row">
      <div className="user-info">
        <div className="user-name">{user.name}</div>
        <div className="user-email">{user.email}</div>
        <div className="user-meta">
          <span className="user-role">{user.role}</span>
          <span className="user-location">• {user.location}</span>
        </div>
        <div className="user-last-active">Last active: {user.lastActive}</div>
      </div>
      
      <div className="user-actions">
        <div className={`status-badge ${user.status.toLowerCase()}`}>
          {user.status}
        </div>
        
        <div className="action-buttons">
      <button
            className="action-button"
            onClick={() => {
        const idx = roles.indexOf(user.role);
        const opts = roles.slice(0, Math.max(idx, 0));
        setRoleOptions(opts);
              setTargetUser(user);
              setRoleChangeMode('promote');
              setShowRoleChangeModal(true);
            }}
          >Promote</button>
          <button
            className="action-button"
            onClick={() => {
        const idx = roles.indexOf(user.role);
        const opts = idx >= 0 ? roles.slice(idx + 1) : [];
        setRoleOptions(opts);
              setTargetUser(user);
              setRoleChangeMode('demote');
              setShowRoleChangeModal(true);
            }}
          >Demote</button>
        </div>
      </div>
    </div>
  );

  const handleToggle = (toggleId) => {
    setToggleStates(prev => {
      const newState = {
        ...prev,
        [toggleId]: !prev[toggleId]
      };
      return newState;
    });
  };

  const handleAddInventoryCategory = async (name) => {
    if (!name?.trim()) return;
    try {
      const res = await InventoryCategoriesService.add(businessId, name.trim());
      const created = res.data && (res.data.name || name.trim());
      setInventoryCategories(prev => prev.includes(created) ? prev : [...prev, created]);
      
      // ✅ Notify other components that a category was added
      categoryEventService.categoryAdded(created);
    } catch (e) { 
      console.error('Error adding inventory category:', e);
    }
  };

  const handleDeleteInventoryCategory = async (name) => {
    try {
      // Fetch server list to find id by name, then delete
      const res = await InventoryCategoriesService.list(businessId);
  const match = (res.data || []).find(c => c.name === name);
      if (!match) return;
  await InventoryCategoriesService.remove(match.category_id);
      setInventoryCategories(prev => prev.filter(c => c !== name));
      
      // ✅ Notify other components that a category was deleted
      categoryEventService.categoryDeleted(name);
    } catch (e) { 
      console.error('Error deleting inventory category:', e);
    }
  };

  return (
    <div className="settings-container">
      {/* Header */}
      <div className="settings-header" style={{ marginBottom: '8px' }}>
        <div className="header-title-container">
          <h1 className="header-title">{t('settings.title')}</h1>
        </div>
      </div>

      {/* Settings Search Bar */}
      <div style={{
        padding: '0 0 16px',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f0f0f0',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          gap: '12px'
        }}>
          <FaSearch style={{
            fontSize: '16px',
            color: '#9ca3af',
            flexShrink: 0
          }} />
          <input
            type="text"
            placeholder="Search items..."
            value={settingsSearchQuery}
            onChange={(e) => setSettingsSearchQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              fontFamily: 'Roboto, sans-serif',
              color: '#111827',
              backgroundColor: 'transparent',
              padding: 0,
              lineHeight: '1.4'
            }}
          />
          {settingsSearchQuery && (
            <button
              onClick={() => setSettingsSearchQuery('')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0
              }}
            >
              <FaTimes style={{ fontSize: '14px', color: '#9ca3af' }} />
            </button>
          )}
        </div>
        {settingsSearchQuery && filteredSettingSections.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '20px 0 4px',
            color: '#9ca3af',
            fontSize: '14px',
            fontFamily: 'Roboto, sans-serif'
          }}>
            No settings found for "{settingsSearchQuery}"
          </div>
        )}
      </div>

  <div className="settings-content">
        {/* Settings Sections */}
        {filteredSettingSections.map((section) => (
          <div 
            key={section.id || section.title} 
            className="section-card"
            data-section-id={section.id || section.title}
          >
            <div
              className="section-header"
              onClick={() => toggleSection(section.id || section.title)}
            >
              <div className="section-title-container">
                <span className="section-icon">{section.icon}</span>
                <h3 className="section-title">{section.title}</h3>
              </div>
              {section.expanded ? (
                <FaChevronUp className="chevron-icon" />
              ) : (
                <FaChevronDown className="chevron-icon" />
              )}
            </div>

            {section.expanded && (
              <div className="section-content">
                {section.items.filter((item) => !item.hidden).map((item) => (
                  <div key={item.id}>
                    {renderSettingItem(item)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Revert Closing Stock Modal */}
      {revertModal.open && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container" style={{ maxWidth: 460 }}>
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Revert Closing Stock</h2>
              <button className="sp-close-button" onClick={() => setRevertModal({ open: false, loading: false, busy: false, preview: null, error: null, done: null })}>
                <FaTimes />
              </button>
            </div>

            <div className="sp-modal-content" style={{ textAlign: 'left' }}>
              {revertModal.loading && (
                <p style={{ color: '#666' }}>Loading the latest closing stock…</p>
              )}

              {/* Success */}
              {!revertModal.loading && revertModal.done && (
                <div style={{ padding: '10px 12px', borderRadius: 10, background: '#e7f6ec', border: '1px solid #bde5c8', color: '#116b37' }}>
                  <strong>Reverted.</strong> Inventory has been restored to its state before that count
                  {revertModal.done.restored_prior_count ? ' — the previous count for that date is active again.' : '.'}
                </div>
              )}

              {/* Error (load failure OR blocked reason from a failed POST) */}
              {!revertModal.loading && !revertModal.done && revertModal.error && (
                <div style={{ padding: '12px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
                  {revertModal.error}
                </div>
              )}

              {/* Preview + confirm */}
              {!revertModal.loading && !revertModal.done && !revertModal.error && revertModal.preview && (() => {
                const p = revertModal.preview;
                if (!p.history) {
                  return <p style={{ color: '#666' }}>There is no closing stock to revert.</p>;
                }
                const d = String(p.history.closing_date || '').slice(0, 10);
                const val = Number(p.history.total_value || 0);
                const affected = Array.isArray(p.affected_items) ? p.affected_items : [];
                return (
                  <>
                    <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 12, marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <span style={{ color: '#666' }}>Closing date</span><strong>{d}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <span style={{ color: '#666' }}>Items counted</span><strong>{p.history.total_items}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <span style={{ color: '#666' }}>Recorded value</span><strong>₹{val.toFixed(2)}</strong>
                      </div>
                      {p.code !== 'NO_SNAPSHOT' && p.history.submitted_at && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0 2px', marginTop: 4, borderTop: '1px solid #f0f0f0' }}>
                          <span style={{ color: '#666' }}>Time left to revert</span>
                          <RevertCountdown submittedAt={p.history.submitted_at} windowDays={p.window_days || 7} />
                        </div>
                      )}
                    </div>

                    {/* What changes, and by how much */}
                    {affected.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                          What will change ({affected.length})
                        </div>
                        <div style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 6, padding: '6px 10px', fontSize: 10.5, fontWeight: 700, color: '#888', background: '#f8f8f8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            <span>Item</span>
                            <span style={{ textAlign: 'right' }}>Now</span>
                            <span style={{ textAlign: 'right' }}>After revert</span>
                          </div>
                          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                            {affected.map(a => (
                              <div key={a.item_id} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 6, padding: '7px 10px', fontSize: 13, borderTop: '1px solid #f0f0f0', alignItems: 'center' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.item_name}</span>
                                <span style={{ textAlign: 'right', color: '#374151' }}>{Number(a.now).toFixed(2)} {a.unit}</span>
                                <span style={{ textAlign: 'right', fontWeight: 700, color: Number(a.restored) < Number(a.now) ? '#b91c1c' : '#116b37' }}>
                                  {Number(a.restored).toFixed(2)} {a.unit}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {p.revertible ? (
                      <div style={{ padding: '10px 12px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 13, lineHeight: 1.5 }}>
                        This restores inventory to exactly how it was <strong>before</strong> this count — including any negative stock it cleared. Only the latest count can be reverted.
                      </div>
                    ) : (
                      <div style={{ padding: '12px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 14, lineHeight: 1.5 }}>
                        {p.reason}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                      <button
                        onClick={() => setRevertModal({ open: false, loading: false, busy: false, preview: null, error: null, done: null })}
                        style={{ padding: '10px 18px', borderRadius: 10, border: '1px solid #d1d5db', background: '#f3f4f6', color: '#374151', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Close
                      </button>
                      {p.revertible && (
                        <button
                          onClick={doRevertClosingStock}
                          disabled={revertModal.busy}
                          style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: revertModal.busy ? '#9ca3af' : '#dc2626', color: '#fff', fontWeight: 700, cursor: revertModal.busy ? 'not-allowed' : 'pointer' }}
                        >
                          {revertModal.busy ? 'Reverting…' : 'Revert Closing Stock'}
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Done/error footer close */}
              {!revertModal.loading && (revertModal.done || revertModal.error) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button
                    onClick={() => setRevertModal({ open: false, loading: false, busy: false, preview: null, error: null, done: null })}
                    style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: '#171717', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">{t('settings.select_language')}</h2>
              <button className="sp-close-button" onClick={() => setShowLanguageModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="sp-modal-content">
              <LanguageSelector />
            </div>
          </div>
        </div>
      )}

      {/* Background Image Modal */}
      {showBackgroundImageModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Login Background Image</h2>
              <button className="sp-close-button" onClick={() => setShowBackgroundImageModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="sp-modal-content">
              <div className="profile-form-section">
                <label className="profile-form-label">Upload Background Image</label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px', textAlign: 'left' }}>
                  Choose a high-resolution image for the background on login, signup, and forgot password screens.
                  Max size: 10MB. Recommended: High-resolution landscape images (1920x1080 or higher) for best clarity.
                </p>
                
                {backgroundImagePreview && (
                  <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                    <img 
                      src={backgroundImagePreview} 
                      alt="Background preview" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        borderRadius: '8px',
                        border: '2px solid #ddd'
                      }} 
                    />
                  </div>
                )}
                
                <input 
                  type="file"
                  accept="image/*"
                  onChange={handleBackgroundImageChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    marginBottom: '10px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div className="spm-actions spm-profile-actions">
                <button 
                  className="spm-action-button secondary spm-profile-action-button"
                  onClick={() => setShowBackgroundImageModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="spm-action-button primary spm-profile-action-button"
                  onClick={handleSaveBackgroundImage}
                  disabled={!backgroundImagePreview}
                  style={{
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    cursor: backgroundImagePreview ? 'pointer' : 'not-allowed'
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal (UI only) */}
      {showEditProfileModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Edit Profile</h2>
              <button className="sp-close-button" onClick={() => setShowEditProfileModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="profile-form-section">
                <label className="profile-form-label">Name</label>
                <input 
                  className={`edit-profile-input ${profileForm.name.trim() ? 'filled' : ''} ${profileFormErrors.name ? 'error' : ''}`}
                  value={profileForm.name} 
                  onChange={e => {
                    setProfileForm(prev => ({...prev, name: e.target.value}));
                    setProfileFormErrors(prev => ({...prev, name: ''}));
                  }} 
                  placeholder="Enter Your Name" 
                />
                {profileFormErrors.name && (
                  <span className="profile-form-error">{profileFormErrors.name}</span>
                )}
              </div>
              <div className="profile-form-section">
                <label className="profile-form-label">Email</label>
                <input 
                  className={`iu-input ${inviteUserData.name.trim() ? 'filled' : ''}`}
                  value={profileForm.email} 
                  onChange={e => {
                    setProfileForm(prev => ({...prev, email: e.target.value}));
                    setProfileFormErrors(prev => ({...prev, email: ''}));
                  }}
                  onBlur={() => {
                    if (profileForm.email.trim() && !isValidEmail(profileForm.email)) {
                      setProfileFormErrors(prev => ({...prev, email: 'Please enter a valid email'}));
                    }
                  }}
                  placeholder="Enter Your Email" 
                  type="email" 
                />
                {profileFormErrors.email && (
                  <span className="profile-form-error">{profileFormErrors.email}</span>
                )}
                {profileForm.email.trim() && !isValidEmail(profileForm.email) && !profileFormErrors.email && (
                  <span className="profile-form-error">Please enter a valid email</span>
                )}
              </div>
              <div className="profile-form-section">
                <label className="profile-form-label">Phone</label>
                <input 
                  className={`edit-profile-input ${profileForm.phone.trim() && isValidPhone(profileForm.phone) ? 'filled' : ''} ${profileFormErrors.phone ? 'error' : ''}`}
                  value={profileForm.phone} 
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    if (value.length <= 10) {
                      setProfileForm(prev => ({...prev, phone: value}));
                      setProfileFormErrors(prev => ({...prev, phone: ''}));
                    }
                  }}
                  onBlur={() => {
                    if (profileForm.phone.trim() && !isValidPhone(profileForm.phone)) {
                      setProfileFormErrors(prev => ({...prev, phone: 'Please enter a 10 digit phone number'}));
                    }
                  }}
                  placeholder="Enter Your Phone" 
                  type="tel"
                  maxLength="10"
                />
                {profileFormErrors.phone && (
                  <span className="profile-form-error">{profileFormErrors.phone}</span>
                )}
                {profileForm.phone.trim() && !isValidPhone(profileForm.phone) && !profileFormErrors.phone && (
                  <span className="profile-form-error">Please enter a 10 digit phone number</span>
                )}
              </div>
              <div className="spm-actions spm-profile-actions">
                <button 
                  className="spm-action-button secondary spm-profile-action-button" 
                  onClick={() => {
                    setShowEditProfileModal(false);
                    setProfileFormErrors({ name: '', email: '', phone: '' });
                    setProfilePicture(null);
                    // Reset preview to current user's profile picture
                    if (currentUser?.profilePicture) {
                      setProfilePicturePreview(currentUser.profilePicture);
                    } else {
                      setProfilePicturePreview(null);
                    }
                  }}
                >
                  Cancel
                </button>
                <button 
                  className={`spm-action-button primary spm-profile-action-button`}
                  onClick={handleSaveProfile}
                  disabled={!isProfileFormValid() || uploadingProfilePicture}
                  style={{
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    cursor: (isProfileFormValid() && !uploadingProfilePicture) ? 'pointer' : 'not-allowed'
                  }}
                >
                  {uploadingProfilePicture ? 'Uploading...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Methods Modal */}
      {showPaymentMethodsModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Payment Methods</h2>
              <button className="sp-close-button" onClick={() => setShowPaymentMethodsModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="form-section" style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label">New Method</label>
                  <input className="form-input" value={newPaymentLabel} onChange={e => setNewPaymentLabel(e.target.value)} placeholder="e.g., Wallets" />
                </div>
                <button className="invite-button" style={{ maxWidth: 160 }} onClick={async () => {
                  const label = newPaymentLabel.trim();
                  if (!label) return;
                  try {
                    const res = await SettingsService.addPaymentMethod(businessId, { method_name: label });
                    const pm = res.data;
                    // prevent dupes locally
                    setPaymentMethods(prev => prev.some(p => p.id === String(pm.payment_method_id)) ? prev : [...prev, { id: String(pm.payment_method_id), label: pm.method_name }]);
                    setNewPaymentLabel('');
                  } catch (e) {
                    alert(e.message || 'Failed to add payment method');
                  }
                }}>Add</button>
              </div>

              <div className="payments-list">
                {paymentMethods.map(pm => (
                  <div key={pm.id} className="user-row">
                    <div className="user-info">
                      <div className="user-name">{pm.label}</div>
                      <div className="user-meta"><span className="user-role">Method ID: {pm.id}</span></div>
                    </div>
                    <div className="user-actions">
                      <button className="action-button" style={{ border: 'none', background: 'transparent', padding: '8px' }} onClick={async () => {
                        try {
                          await SettingsService.deletePaymentMethod(pm.id);
                          setPaymentMethods(prev => prev.filter(p => p.id !== pm.id));
                        } catch (e) {
                          alert(e.message || 'Failed to delete payment method');
                        }
                      }}>
                        <FaTrash className="action-icon" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* User Management Modal */}
      {showUserManagementModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container full-screen">
            <div className="sp-modal-header">
              <div className="sp-modal-left">
                <button className="sp-close-button sp-back-button" onClick={() => setShowUserManagementModal(false)} aria-label="Back">
                  <FaArrowLeft />
                </button>
                <h2 className="sp-modal-title">Manage Users</h2>
              </div>
            </div>
            <div className="user-management-content">
              {/* Search and Add User */}
              <div className="user-management-actions">
                <div className="search-container">
                  <FaSearch className="search-icon" />
                  <input
                    className="search-input"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button 
                  className="add-user-button"
                  onClick={() => {
                    setShowUserManagementModal(false);
                    setShowInviteUserModal(true);
                  }}
                >
                  <FaUserPlus />
                </button>
              </div>

              {/* Users List */}
              <div className="users-list">
                {usersLoading && (
                  <div style={{ padding: 16, color: '#6b7280' }}>Loading users…</div>
                )}
                {usersError && !usersLoading && (
                  <div style={{ padding: 16, color: '#dc2626' }}>Failed to load users: {usersError}</div>
                )}
                {!usersLoading && !usersError && filteredUsers.length === 0 && (
                  <div style={{ padding: 16, color: '#6b7280' }}>No users found.</div>
                )}
                {!usersLoading && !usersError && filteredUsers.map(renderUserRow)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteUserModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Invite New User</h2>
              <button className="sp-close-button" onClick={() => setShowInviteUserModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="sp-modal-content">

              <div className="profile-form-section">
                <label className="profile-form-label">Name</label>
                <input
                  className={`invite-user-input ${inviteUserData.name.trim() ? 'filled' : ''}`}
                  value={inviteUserData.name}
                  onChange={(e) => {
                    setInviteUserData(prev => ({ ...prev, name: e.target.value }));
                    setInviteUserErrors(prev => ({ ...prev, name: '' }));
                  }}
                  placeholder="Enter full name (e.g., John Doe)"
                />
                {inviteUserErrors.name && (
                  <span className="profile-form-error">{inviteUserErrors.name}</span>
                )}
              </div>

              <div className="profile-form-section">
                <label className="profile-form-label">Role</label>
                {waiterLimit > 0 && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginTop: '4px',
                    marginBottom: '8px'
                  }}>
                    Waiter Limit from Owner Dashboard: <strong>{waiterLimit}</strong>
                  </div>
                )}
                <div className="role-grid" style={{ marginTop: '12px' }}>
                  {roles.map((role) => {
                    const isDefaultRole = defaultRoles.includes(role);
                    return (
                      <div
                        key={role}
                        className={`role-option ${inviteUserData.role === role ? 'selected' : ''}`}
                        style={{ position: 'relative', paddingRight: isDefaultRole ? '16px' : '28px', cursor: 'pointer' }}
                        onClick={() => {
                          setInviteUserData(prev => ({ ...prev, role }));
                          setInviteUserErrors(prev => ({ ...prev, role: '' }));
                        }}
                      >
                        <span className="role-option-text">
                          {role}
                        </span>
                        {!isDefaultRole && (
                          <button
                            className="role-delete-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRoles(prev => prev.filter(r => r !== role));
                              if (inviteUserData.role === role) {
                                setInviteUserData(prev => ({ ...prev, role: '' }));
                              }
                            }}
                            style={{
                              position: 'absolute',
                              top: '-6px',
                              right: '-6px',
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              backgroundColor: '#111827',
                              border: 'none',
                              color: 'white',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {inviteUserErrors.role && (
                  <span className="profile-form-error">{inviteUserErrors.role}</span>
                )}
                {waiterLimitError && (
                  <div style={{ 
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#FEE2E2',
                    borderRadius: '8px',
                    border: '1px solid #DC2626',
                    fontSize: '13px',
                    color: '#DC2626',
                    fontWeight: '500'
                  }}>
                    ⚠️ {waiterLimitError}
                  </div>
                )}
              </div>

              <button 
                className={`invite-button ${isInviteFormValid() ? 'enabled' : 'disabled'}`}
                onClick={handleInviteUser}
                disabled={!isInviteFormValid()}
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Credentials Modal */}
      {showCredentialsModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container" style={{ maxWidth: '600px', fontFamily: 'Roboto, sans-serif' }}>
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">User Created Successfully!</h2>
              <button 
                className="sp-close-button" 
                onClick={() => setShowCredentialsModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="sp-modal-content">
              <div style={{ 
                padding: '0 20px 20px 20px', 
                background: 'transparent',
                borderRadius: '12px'
              }}>
                <h3 style={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  marginBottom: '16px',
                  marginTop: '0',
                  color: '#000000',
                  fontFamily: 'Roboto, sans-serif',
                  textAlign: 'left'
                }}>
                  Generated Credentials
                </h3>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '15px', 
                    fontWeight: 600, 
                    color: '#000000',
                    marginBottom: '6px',
                    textAlign: 'left'
                  }}>
                    Email
                  </label>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB'
                  }}>
                    <input
                      type="text"
                      value={generatedCredentials.email}
                      readOnly
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        fontFamily: 'Roboto, sans-serif',
                        color: '#1F2937',
                        background: 'transparent'
                      }}
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(generatedCredentials.email);
                        alert('Email copied to clipboard!');
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#000000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '15px', 
                    fontWeight: 600, 
                    color: '#000000',
                    marginBottom: '6px',
                    textAlign: 'left'
                  }}>
                    Password
                  </label>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB'
                  }}>
                    <input
                      type="text"
                      value={generatedCredentials.password}
                      readOnly
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        fontFamily: 'Roboto, sans-serif',
                        color: '#1F2937',
                        background: 'transparent'
                      }}
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(generatedCredentials.password);
                        alert('Password copied to clipboard!');
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#000000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button
                onClick={() => setShowCredentialsModal(false)}
                style={{
                  width: '33.33%',
                  minWidth: '80px',
                  padding: '14px',
                  background: '#000000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Permissions Modal */}
      {showRolePermissionsModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container full-screen">
            <div className="sp-modal-header">
              <div className="sp-modal-left">
                <button className="sp-close-button sp-back-button" onClick={() => setShowRolePermissionsModal(false)} aria-label="Back">
                  <FaArrowLeft />
                </button>
                <h2 className="sp-modal-title">Roles & Permissions</h2>
              </div>
            </div>

            <div className="permissions-content role-permissions-content">
              {/* Module Access Badge */}
              <div className="role-selector">
                <label className="role-selector-label">Select Role to View Permissions:</label>
                <div className="role-scroll-view">
                  {roles.map((role) => {
                    const actualPerms = actualPermissions[role] || [];
                    return (
                      <div
                        key={role}
                        className={`role-selector-option ${selectedRole === role ? 'selected' : ''}`}
                        onClick={() => setSelectedRole(role)}
                      >
                        <span className="role-selector-text">
                          {role}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Loading State */}
              {rolePermissionsLoading && selectedRole && (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: '#6B7280',
                  fontSize: '14px'
                }}>
                  Loading permissions for {selectedRole}...
                </div>
              )}

              {/* Actual Permissions from Backend with Toggle for Owner */}
              {!rolePermissionsLoading && selectedRole && allAvailablePermissions.length > 0 && (
                <div className="role-permissions-layout">
                  <div style={{ 
                    padding: '12px 16px',
                    background: '#EFF6FF',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
                      {(userRole?.toLowerCase().includes('owner') || userRole?.toLowerCase().includes('admin')) ? 'Manage' : '✅ Enabled'} Permissions for {selectedRole}
                    </span>
                    <span style={{ 
                      fontSize: '13px', 
                      fontWeight: 600, 
                      color: '#1E40AF',
                      background: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px'
                    }}>
                      {enabledPermissionIds.length} / {allAvailablePermissions.length}
                    </span>
                  </div>

                  {/* Scrollable permissions container */}
                  <div className="role-permissions-scroll">
                    {/* Group permissions by module */}
                    {Object.entries(
                    allAvailablePermissions.reduce((acc, perm) => {
                      const module = perm.module_name || 'General';
                      if (!acc[module]) acc[module] = [];
                      acc[module].push(perm);
                      return acc;
                    }, {})
                  ).map(([moduleName, perms]) => (
                    <div key={moduleName} style={{ marginBottom: '24px' }}>
                      <div style={{ 
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '12px',
                        padding: '0',
                        background: 'transparent',
                        borderRadius: '0'
                      }}>
                        {moduleName}
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0',
                        padding: '0'
                      }}>
                        {perms.map((perm) => {
                          const isEnabled = enabledPermissionIds.includes(perm.permission_id);
                          // Check if user is Owner or Admin
                          const roleToCheck = userRole?.toLowerCase() || '';
                          const canToggle = roleToCheck.includes('owner') || roleToCheck.includes('admin');
                          
                          return (
                            <div 
                              key={perm.permission_id}
                              className="permission-row"
                            >
                              <div className="permission-text">
                                <div style={{ 
                                  fontWeight: 600, 
                                  fontSize: '14px',
                                  color: isEnabled ? '#1F2937' : '#6B7280',
                                  marginBottom: '4px'
                                }}>
                                  {perm.display_name || perm.permission_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </div>
                                {perm.description && (
                                  <div style={{ 
                                    fontSize: '12px',
                                    color: '#6B7280'
                                  }}>
                                    {perm.description}
                                  </div>
                                )}
                              </div>
                              
                              {canToggle ? (
                                <label className="switch">
                                  <input 
                                    type="checkbox" 
                                    checked={isEnabled}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      togglePermission(perm.permission_id);
                                    }}
                                  />
                                  <span className="slider round"></span>
                                </label>
                              ) : (
                                <span style={{ 
                                  fontSize: '16px',
                                  marginLeft: '4px'
                                }}>
                                  {isEnabled ? '✅' : '❌'}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  </div>

                  {/* Save Button for Owner or Admin */}
                  {(userRole?.toLowerCase().includes('owner') || userRole?.toLowerCase().includes('admin')) && (
                    <div className="spm-actions spm-profile-actions role-permissions-sticky-actions">
                      <button
                        className="spm-action-button secondary spm-profile-action-button"
                        onClick={() => {
                          // Reload to reset changes
                          loadRolePermissions(selectedRole);
                        }}
                        disabled={permissionsSaving}
                      >
                        Cancel
                      </button>
                      <button
                        className="spm-action-button primary spm-profile-action-button"
                        onClick={saveRolePermissions}
                        disabled={permissionsSaving}
                        style={{
                          backgroundColor: '#000000',
                          color: '#ffffff'
                        }}
                      >
                        {permissionsSaving ? 'Saving...' : 'Save Permissions'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Wastage Reasons Modal */}
      {showWastageReasonsModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container full-screen wastage-reasons-modal">
            <div className="sp-modal-header">
              <div className="sp-modal-left">
                <button className="sp-back-button" onClick={() => setShowWastageReasonsModal(false)}>
                  <FaArrowLeft />
                </button>
                <h2 className="sp-modal-title">Wastage Reasons</h2>
              </div>
              <button className="sp-close-button" onClick={() => setShowWastageReasonsModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="permissions-content">
              <div className="form-section" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <label className="form-label">Reason Label</label>
                  <input className="form-input" value={newReasonLabel} onChange={e => setNewReasonLabel(e.target.value)} placeholder="e.g., Spillage" />
                </div>
                <div style={{ flex: 1, minWidth: 280, position: 'relative' }}>
                  <label className="form-label">Category</label>
                  <div 
                    className="custom-select-trigger" 
                    onClick={() => setWasteCategoryDropdownOpen(!wasteCategoryDropdownOpen)}
                  >
                    <span>{newReasonCategory}</span>
                    <span className="custom-chevron-down" />
                  </div>
                  {wasteCategoryDropdownOpen && (
                    <div className="custom-select-dropdown">
                      <div 
                        className="custom-select-option" 
                        onClick={() => {
                          setNewReasonCategory('Dish Waste');
                          setWasteCategoryDropdownOpen(false);
                        }}
                      >
                        Dish Waste
                      </div>
                      <div 
                        className="custom-select-option" 
                        onClick={() => {
                          setNewReasonCategory('General Waste');
                          setWasteCategoryDropdownOpen(false);
                        }}
                      >
                        General Waste
                      </div>
                    </div>
                  )}
                </div>
                <button
                  className="invite-button"
                  onClick={async () => {
                    if (!newReasonLabel.trim()) { alert('Please Enter A Reason Label'); return; }
                    try {
                      const res = await WastageService.createWastageReason({ reason_label: newReasonLabel.trim(), reason_category: newReasonCategory });
                      setWastageReasons(prev => [...prev, res.data]);
                      setNewReasonLabel('');
                      // 🔔 Broadcast event to notify other components
                      wastageReasonEventService.reasonAdded(res.data);
                    } catch (e) {
                      alert(e.message || 'Failed to add reason');
                    }
                  }}
                >Add Reason</button>
              </div>

              <div className="users-list wastage-reasons-list">
                {wastageReasons.map(r => (
                  <div key={r.reason_id} className="user-row">
                    <div className="user-info">
                      <div className="user-name">{r.reason_label}</div>
                      <div className="user-meta"><span className="user-role">{r.reason_category || 'General'}</span></div>
                    </div>
                    <div className="user-actions">
                      <button
                        className="action-button"
                        title="Delete"
                        onClick={async () => {
                          if (!(await showAppConfirm('Delete this reason?'))) return;
                          try {
                            await WastageService.deleteWastageReason(r.reason_id);
                            setWastageReasons(prev => prev.filter(x => x.reason_id !== r.reason_id));
                            // 🔔 Broadcast event to notify other components
                            wastageReasonEventService.reasonDeleted(r.reason_id);
                          } catch (e) {
                            alert(e.message || 'Failed to delete reason');
                          }
                        }}
                      >
                        <FaTrash className="action-icon" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ padding: 12, color: '#666' }}>
                Changes reflect immediately in Stock Out form reasons.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deviation Selector Modal */}
      {showDeviationModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Select Alert Deviation</h2>
              <button className="sp-close-button" onClick={() => setShowDeviationModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="form-section" style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label">Add Manually</label>
                  <input 
                    className="form-input" 
                    type="number"
                    value={customDeviation} 
                    onChange={e => setCustomDeviation(e.target.value)} 
                    placeholder="e.g., 20" 
                  />
                </div>
                <button 
                  className="invite-button" 
                  style={{ maxWidth: 160 }} 
                  onClick={() => {
                    const percent = parseInt(customDeviation);
                    if (percent && percent > 0 && percent <= 100) {
                      onSelectDeviation(percent);
                      setCustomDeviation('');
                    }
                  }}
                >
                  Add
                </button>
              </div>
              
              <div className="role-grid deviation-options" style={{ marginTop: 24 }}>
                {[5, 10, 15].map(p => (
                  <div
                    key={p}
                    className={`role-option ${deviation === p ? 'selected' : ''}`}
                    onClick={() => onSelectDeviation(p)}
                  >
                    <span className="role-option-text">{p}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Categories Modal (UI-only) */}
      {showInventoryCategoriesModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Inventory Categories</h2>
              <button className="sp-close-button" onClick={() => setShowInventoryCategoriesModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="form-section" style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label" style={{ textAlign: 'left', display: 'block' }}>New Category</label>
                  <input className="form-input" style={{ border: '2px solid #000000' }} value={newCategoryLabel} onChange={e => setNewCategoryLabel(e.target.value)} placeholder="e.g., Spices" />
                </div>
                <button className="invite-button" style={{ maxWidth: 160 }} onClick={async () => {
                  const label = newCategoryLabel.trim();
                  if (!label) return;
                  if (inventoryCategories.some(c => c.toLowerCase() === label.toLowerCase())) { alert('Already Exists'); return; }
                  await handleAddInventoryCategory(label);
                  setNewCategoryLabel('');
                }}>Add</button>
              </div>
              <div className="categories-list">
                {inventoryCategories.map(cat => (
                  <div key={cat} className="user-row">
                    <div className="user-info">
                      <div className="user-name">{cat}</div>
                    </div>
                    <div className="user-actions">
                      <button className="action-button" style={{ border: 'none', background: 'transparent', padding: '8px' }} onClick={async () => { await handleDeleteInventoryCategory(cat); }}>
                        <FaTrash className="action-icon" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal (multi-step, UI-only) */}
      {showChangePasswordModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Change Password</h2>
              <button className="sp-close-button" onClick={() => setShowChangePasswordModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              {changePwdStep === 1 && (
                <>
                  <div className="profile-form-section">
                    <label className="profile-form-label">Current Password</label>
                    <div className="password-input-wrapper">
                      <input 
                        className={`profile-form-input password-input ${oldPassword.trim() ? 'filled' : ''}`}
                        type={showOldPassword ? "text" : "password"}
                        value={oldPassword} 
                        onChange={e => setOldPassword(e.target.value)} 
                        placeholder="Enter Current Password" 
                      />
                      <button 
                        type="button"
                        className="password-toggle-button"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                      >
                        {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div className="spm-actions spm-profile-actions">
                    <button 
                      className="spm-action-button secondary spm-profile-action-button" 
                      onClick={() => {
                        setShowChangePasswordModal(false);
                        setShowOldPassword(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      className={`spm-action-button primary spm-profile-action-button ${oldPassword.trim() ? 'enabled' : 'disabled'}`}
                      onClick={async () => {
                        if (!oldPassword.trim()) { alert('Please Enter Current Password'); return; }
                        try {
                          await authService.verifyPassword(oldPassword);
                          setChangePwdStep(2);
                        } catch (err) {
                          alert(err.message || 'Incorrect password');
                        }
                      }}
                      disabled={!oldPassword.trim()}
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}
              {changePwdStep === 2 && (
                <>
                  <div className="profile-form-section">
                    <label className="profile-form-label">New Password</label>
                    <div className="password-input-wrapper">
                      <input 
                        className={`profile-form-input password-input ${newPassword.trim() ? 'filled' : ''}`}
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword} 
                        onChange={e => setNewPassword(e.target.value)} 
                        placeholder="Enter New Password" 
                      />
                      <button 
                        type="button"
                        className="password-toggle-button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div className="profile-form-section">
                    <label className="profile-form-label">Re-enter New Password</label>
                    <div className="password-input-wrapper">
                      <input 
                        className={`profile-form-input password-input ${confirmPassword.trim() ? 'filled' : ''}`}
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword} 
                        onChange={e => setConfirmPassword(e.target.value)} 
                        placeholder="Re-enter new password" 
                      />
                      <button 
                        type="button"
                        className="password-toggle-button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div className="spm-actions spm-profile-actions">
                    <button 
                      className="spm-action-button secondary spm-profile-action-button" 
                      onClick={() => {
                        setChangePwdStep(1);
                        setShowNewPassword(false);
                        setShowConfirmPassword(false);
                      }}
                    >
                      Back
                    </button>
                    <button 
                      className={`spm-action-button primary spm-profile-action-button ${newPassword.trim() && confirmPassword.trim() && newPassword === confirmPassword ? 'enabled' : 'disabled'}`}
                      onClick={handleChangePassword}
                      disabled={!newPassword.trim() || !confirmPassword.trim() || newPassword !== confirmPassword}
                    >
                      Save
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal (Promote/Demote) */}
      {showRoleChangeModal && targetUser && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">{roleChangeMode === 'promote' ? 'Promote User' : 'Demote User'}</h2>
              <button className="sp-close-button" onClick={() => setShowRoleChangeModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="form-section">
                <div className="role-grid">
                  {roleOptions.length === 0 && (
                    <div style={{ color: '#6b7280' }}>No available roles.</div>
                  )}
                  {roleOptions.map(r => (
                    <div
                      key={r}
                      className={`role-option ${r === targetUser.role ? 'selected' : ''}`}
                      onClick={async () => {
                        try {
                          const roleObj = allRoles.find(x => x.role_name === r);
                          if (!roleObj) throw new Error('Role not found');
                          await UserService.updateUserRole(targetUser.id, roleObj.role_id, businessId);
                        } catch (err) {
                          alert(err.message || 'Failed to update role');
                          return;
                        }
                        // Update UI and reload list
                        setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, role: r } : u));
                        setShowRoleChangeModal(false);
                        loadUsers();
                      }}
                    >
                      <span className="role-option-text">{r}</span>
                    </div>
                  ))}
                </div>
                {rolesError && <div style={{ color: '#dc2626', marginTop: 8 }}>{rolesError}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Default Payment Terms Modal */}
      {showPaymentTermsModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Default Payment Terms</h2>
              <button className="sp-close-button" onClick={() => setShowPaymentTermsModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="form-section" style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label">Add Manually</label>
                  <input 
                    className="form-input" 
                    type="number"
                    value={customPaymentTerm} 
                    onChange={e => setCustomPaymentTerm(e.target.value)} 
                    placeholder="e.g., 45" 
                  />
                </div>
                <button 
                  className="invite-button" 
                  style={{ maxWidth: 160 }} 
                  onClick={() => {
                    const days = parseInt(customPaymentTerm);
                    if (days && days > 0) {
                      onSelectPaymentTerm(days);
                      setCustomPaymentTerm('');
                    }
                  }}
                >
                  Add
                </button>
              </div>
              
              <div className="role-grid default-payments-options" style={{ marginTop: 24 }}>
                {[7, 14, 21, 30].map(d => (
                  <div
                    key={d}
                    className={`role-option ${paymentTermDays === d ? 'selected' : ''}`}
                    onClick={() => onSelectPaymentTerm(d)}
                  >
                    <span className="role-option-text">{d} Days</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operating Hours Modal */}
      {showOperatingHoursModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container full-screen">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Operating Hours</h2>
              <button 
                className="sp-close-button" 
                onClick={() => setShowOperatingHoursModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="operating-hours-modal-content">
              <div className="operating-hours-content">
                {Object.entries(operatingHours).map(([day, dayData]) => (
                  <div key={day} className="operating-day-section">
                    <div className="day-header">
                      <span className="day-name">{day}</span>
                      <div className="day-toggle">
                        <label className="switch">
                          <input 
                            type="checkbox" 
                            checked={dayData.isOpen}
                            onChange={() => toggleDayStatus(day)}
                          />
                          <span className="slider round"></span>
                        </label>
                        <span className="day-status">{dayData.isOpen ? 'Open' : 'Closed'}</span>
                      </div>
                    </div>
                    
                    {dayData.isOpen && (
                      <div className="shifts-container">
                        {Object.entries(dayData.shifts).map(([shiftName, shiftData]) => (
                          <div key={shiftName} className="shift-row">
                            <div className="shift-header">
                              <span className="shift-name">
                                {shiftName.charAt(0).toUpperCase() + shiftName.slice(1)}
                              </span>
                              {shiftData.enabled && (
                                <button 
                                  className="remove-shift-button"
                                  onClick={() => disableShift(day, shiftName)}
                                >
                                  <FaTimes />
                                </button>
                              )}
                            </div>
                            
                            {shiftData.enabled ? (
                              <div className="time-inputs">
                                <div className="time-input-group">
                                  <label>Opens at</label>
                                  <input
                                    type="text"
                                    className="time-input"
                                    value={shiftData.start}
                                    onChange={(e) => updateShiftTime(day, shiftName, 'start', e.target.value)}
                                    placeholder="9:00 AM"
                                  />
                                </div>
                                
                                <div className="time-input-group">
                                  <label>Closes at</label>
                                  <input
                                    type="text"
                                    className="time-input"
                                    value={shiftData.end}
                                    onChange={(e) => updateShiftTime(day, shiftName, 'end', e.target.value)}
                                    placeholder="5:00 PM"
                                  />
                                </div>
                              </div>
                            ) : (
                              <button 
                                className="add-hours-button"
                                onClick={() => enableShift(day, shiftName)}
                              >
                                Add hours
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="spm-actions">
                <button 
                  className="spm-action-button secondary"
                  onClick={() => setShowOperatingHoursModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="spm-action-button primary"
                  onClick={saveOperatingHours}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Settings Modal */}
      {showPaymentSettingsModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Payment Settings</h2>
              <button className="sp-close-button" onClick={() => setShowPaymentSettingsModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              {loadingPaymentSettings ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading payment settings...
                </div>
              ) : (
                <div className="payment-settings-content">
                  <p className="section-description" style={{ marginBottom: '20px', color: '#666' }}>
                    Choose how customers pay online. Direct UPI payments have <strong>zero transaction fees</strong>.
                  </p>

                  {/* Payment Method Selection */}
                  <div className="profile-form-section">
                    <label className="profile-form-label">Payment Method</label>
                    <div className="sp-payment-method-select-wrapper">
                      <div
                        className="sp-payment-method-select-trigger"
                        onClick={() => setPaymentMethodDropdownOpen(!paymentMethodDropdownOpen)}
                      >
                        <span>
                          {paymentSettings.paymentMethod === 'razorpay'
                            ? 'Razorpay (Payment Gateway)'
                            : 'Direct UPI (Zero Fees)'}
                        </span>
                        <svg
                          className={`sp-payment-method-select-arrow ${paymentMethodDropdownOpen ? 'open' : ''}`}
                          width="14"
                          height="14"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path d="M2 4L6 8L10 4" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      {paymentMethodDropdownOpen && (
                        <div className="sp-payment-method-select-dropdown">
                          <div
                            className={`sp-payment-method-select-option ${paymentSettings.paymentMethod === 'razorpay' ? 'selected' : ''}`}
                            onClick={() => {
                              setPaymentSettings({ ...paymentSettings, paymentMethod: 'razorpay' });
                              setPaymentMethodDropdownOpen(false);
                            }}
                          >
                            Razorpay (Payment Gateway)
                          </div>
                          <div
                            className={`sp-payment-method-select-option ${paymentSettings.paymentMethod === 'upi' ? 'selected' : ''}`}
                            onClick={() => {
                              setPaymentSettings({ ...paymentSettings, paymentMethod: 'upi' });
                              setPaymentMethodDropdownOpen(false);
                            }}
                          >
                            Direct UPI (Zero Fees)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* UPI Configuration (only show if UPI selected) */}
                  {paymentSettings.paymentMethod === 'upi' && (
                    <>
                      <div className="profile-form-section">
                        <label className="profile-form-label">Your UPI ID / VPA</label>
                        <input
                          className="profile-form-input"
                          value={paymentSettings.vpa}
                          onChange={(e) => setPaymentSettings({...paymentSettings, vpa: e.target.value})}
                          placeholder="9876543210@paytm or merchant@ybl or user@oksbi"
                          type="text"
                          style={{ border: '2px solid #000000', padding: '12px', borderRadius: '8px' }}
                        />
                      </div>

                      {/* Preview */}
                      {paymentSettings.vpa && (
                        <div className="profile-form-section">
                          <label className="profile-form-label">Preview UPI Link</label>
                          <div style={{ 
                            padding: '12px', 
                            background: '#f5f5f5', 
                            borderRadius: '8px',
                            fontSize: '12px',
                            wordBreak: 'break-all',
                            color: '#333',
                            border: '1px solid #ddd'
                          }}>
                            {`upi://pay?pa=${paymentSettings.vpa}&pn=${paymentSettings.businessName?.replace(/ /g, '%20') || 'YourRestaurant'}&am={AMOUNT}&cu=INR`}
                          </div>
                          <small style={{ color: '#28a745', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                            ✅ When customers click "Pay Online", this link will open their UPI app with payment pre-filled.
                          </small>
                        </div>
                      )}

                      {/* Help Text */}
                      <div style={{ 
                        background: '#fff', 
                        border: '1px solid #cdcdcd',
                        borderRadius: '8px',
                        padding: '16px',
                        marginTop: '16px'
                      }}>
                        <strong style={{ color: '#000', display: 'block', marginBottom: '8px', textAlign: 'left' }}>
                          How to find your UPI ID:
                        </strong>
                        <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#333', textAlign: 'left' }}>
                          <li style={{ marginBottom: '6px' }}><strong>PhonePe:</strong> Open app → Profile → Payment Address (e.g., merchant@ybl)</li>
                          <li style={{ marginBottom: '6px' }}><strong>Google Pay:</strong> Profile Picture → Bank Account → UPI ID (e.g., user@oksbi)</li>
                          <li style={{ marginBottom: '6px' }}><strong>Paytm:</strong> Profile → Payment Settings → UPI ID (e.g., 9876543210@paytm)</li>
                          <li style={{ color: '#000', fontWeight: 'bold' }}>Must include @provider suffix (e.g., @paytm, @ybl, @oksbi)</li>
                        </ul>
                      </div>

                      {/* QR Code Note */}
                      <div style={{ 
                        background: '#fff', 
                        border: '1px solid #cdcdcd',
                        borderRadius: '8px',
                        padding: '16px',
                        marginTop: '16px'
                      }}>
                        <strong style={{ color: '#000', display: 'block', marginBottom: '8px', textAlign: 'left' }}>
                          How customers will pay:
                        </strong>
                        <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#000', textAlign: 'left' }}>
                          <li style={{ marginBottom: '6px' }}>Customer clicks "Pay Online" → QR code appears</li>
                          <li style={{ marginBottom: '6px' }}>Customer scans QR with UPI app → Payment screen opens</li>
                          <li style={{ marginBottom: '6px' }}>Amount is pre-filled automatically</li>
                          <li>After payment, customer confirms completion</li>
                        </ul>
                      </div>
                    </>
                  )}

                  {/* Razorpay Info */}
                  {paymentSettings.paymentMethod === 'razorpay' && (
                    <div style={{ 
                      background: '#fff', 
                      border: '1px solid #cdcdcd',
                      borderRadius: '8px',
                      padding: '16px',
                      marginTop: '16px'
                    }}>
                      <strong style={{ color: '#000', display: 'block', marginBottom: '8px', textAlign: 'left' }}>
                        Razorpay charges transaction fees:
                      </strong>
                      <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#000', textAlign: 'left' }}>
                        <li>2% + GST per transaction</li>
                        <li>Settlement in 2-3 business days</li>
                      </ul>
                    </div>
                  )}

                  <div className="spm-actions spm-profile-actions" style={{ marginTop: '24px' }}>
                    <button 
                      className="spm-action-button secondary spm-profile-action-button" 
                      onClick={() => setShowPaymentSettingsModal(false)}
                      disabled={loadingPaymentSettings}
                    >
                      Cancel
                    </button>
                    <button 
                      className="spm-action-button primary spm-profile-action-button enabled"
                      onClick={handleSavePaymentSettings}
                      disabled={loadingPaymentSettings}
                    >
                      {loadingPaymentSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Menu Organization Modal */}
      {showMenuOrganizationModal && (
        <MenuOrganization
          isOpen={showMenuOrganizationModal}
          onClose={() => setShowMenuOrganizationModal(false)}
          onComplete={() => setShowMenuOrganizationModal(false)}
        />
      )}

      {/* Menu Categories Modal */}
      {showMenuCategoriesModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Menu Categories</h2>
              <button className="sp-close-button" onClick={() => setShowMenuCategoriesModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="form-section" style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label" style={{ textAlign: 'left', display: 'block' }}>Add New Category</label>
                  <input 
                    className="form-input" 
                    style={{ border: '2px solid #000000' }} 
                    value={newCategory.name} 
                    onChange={(e) => setNewCategory({...newCategory, name: e.target.value})} 
                    placeholder="Category Name" 
                  />
                </div>
                <button 
                  className="invite-button" 
                  style={{ maxWidth: 160 }} 
                  onClick={() => {
                    if (newCategory.name.trim()) {
                      setMenuCategories([...menuCategories, {
                        id: Date.now(),
                        name: newCategory.name,
                        color: newCategory.color,
                        displayOrder: parseInt(newCategory.displayOrder) || menuCategories.length + 1
                      }]);
                      setNewCategory({ name: '', color: '#FF6B6B', displayOrder: '' });
                    }
                  }}
                >
                  Add
                </button>
              </div>
              <div className="categories-list">
                {menuCategories
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((category) => (
                    <div key={category.id} className="user-row">
                      <div className="user-info">
                        <div className="user-name">{category.name}</div>
                      </div>
                      <div className="user-actions">
                        <button 
                          className="action-button" 
                          style={{ border: 'none', background: 'transparent', padding: '8px' }} 
                          onClick={() => {
                            setMenuCategories(menuCategories.filter(c => c.id !== category.id));
                          }}
                        >
                          <FaTrash className="action-icon" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tax Rates Modal */}
      {showTaxRatesModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container full-screen">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Tax Rates</h2>
              <button className="sp-close-button" onClick={() => setShowTaxRatesModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="tax-modal-content">
              <div className="tax-rates-content">
                {/* Add New Tax Section */}
                <div className="add-tax-section">
                  <h3 className="subsection-title">Define New Tax Component</h3>
                  <div className="tax-form">
                    <div className="form-row">
                      <div className="form-field">
                        <label className="form-label">Tax Name</label>
                        <input
                          className="form-input"
                          style={{ border: '2px solid #000000' }}
                          value={newTax.name}
                          onChange={(e) => setNewTax({...newTax, name: e.target.value})}
                          placeholder="e.g., GST - CGST"
                        />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Rate (%)</label>
                        <input
                          type="number"
                          className="form-input"
                          style={{ border: '2px solid #000000' }}
                          value={newTax.rate}
                          onChange={(e) => setNewTax({...newTax, rate: e.target.value})}
                          placeholder="9"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-field">
                        <label className="form-label">Type of Tax</label>
                        <div className="radio-group">
                          <label className="radio-label">
                            <input
                              type="radio"
                              checked={newTax.type === 'standard'}
                              onChange={() => setNewTax({...newTax, type: 'standard'})}
                            />
                            <span>Standard Tax</span>
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              checked={newTax.type === 'beverage'}
                              onChange={() => setNewTax({...newTax, type: 'beverage'})}
                            />
                            <span>Beverage Tax</span>
                          </label>
                        </div>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Inclusion Rule</label>
                        <div className="radio-group">
                          <label className="radio-label">
                            <input
                              type="radio"
                              checked={newTax.inclusionRule === 'exclusive'}
                              onChange={() => setNewTax({...newTax, inclusionRule: 'exclusive'})}
                            />
                            <span>Added to Price</span>
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              checked={newTax.inclusionRule === 'inclusive'}
                              onChange={() => setNewTax({...newTax, inclusionRule: 'inclusive'})}
                            />
                            <span>Included in Price</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <button
                      className="add-button"
                      onClick={() => {
                        if (newTax.name.trim() && newTax.rate) {
                          setTaxRates([...taxRates, {
                            id: Date.now(),
                            name: newTax.name,
                            rate: parseFloat(newTax.rate),
                            type: newTax.type,
                            inclusionRule: newTax.inclusionRule,
                            isActive: true
                          }]);
                          setNewTax({ name: '', rate: '', type: 'standard', inclusionRule: 'exclusive', isActive: true });
                        }
                      }}
                    >
                      Add Tax Component
                    </button>
                  </div>
                </div>

                {/* Existing Tax Rates List */}
                <div className="tax-list-section">
                  <h3 className="subsection-title">Tax Component List</h3>
                  <div className="tax-rates-list">
                    {taxRates.map((tax) => (
                      <div key={tax.id} className="tax-rate-card">
                        {editingTax?.id === tax.id ? (
                          <div className="tax-edit-form">
                            <input
                              className="form-input"
                              value={editingTax.name}
                              onChange={(e) => setEditingTax({...editingTax, name: e.target.value})}
                            />
                            <input
                              type="number"
                              className="form-input small"
                              value={editingTax.rate}
                              onChange={(e) => setEditingTax({...editingTax, rate: e.target.value})}
                              step="0.01"
                            />
                            <div style={{ position: 'relative' }}>
                              <div 
                                className="custom-select-trigger" 
                                onClick={() => setTaxTypeDropdownOpen(!taxTypeDropdownOpen)}
                              >
                                <span>{editingTax.type === 'standard' ? 'Standard Tax' : 'Beverage Tax'}</span>
                                <span className="custom-chevron-down" />
                              </div>
                              {taxTypeDropdownOpen && (
                                <div className="custom-select-dropdown">
                                  <div 
                                    className="custom-select-option" 
                                    onClick={() => {
                                      setEditingTax({...editingTax, type: 'standard'});
                                      setTaxTypeDropdownOpen(false);
                                    }}
                                  >
                                    Standard Tax
                                  </div>
                                  <div 
                                    className="custom-select-option" 
                                    onClick={() => {
                                      setEditingTax({...editingTax, type: 'beverage'});
                                      setTaxTypeDropdownOpen(false);
                                    }}
                                  >
                                    Beverage Tax
                                  </div>
                                </div>
                              )}
                            </div>
                            <div style={{ position: 'relative' }}>
                              <div 
                                className="custom-select-trigger" 
                                onClick={() => setTaxInclusionDropdownOpen(!taxInclusionDropdownOpen)}
                              >
                                <span>{editingTax.inclusionRule === 'exclusive' ? 'Added to Price' : 'Included in Price'}</span>
                                <span className="custom-chevron-down" />
                              </div>
                              {taxInclusionDropdownOpen && (
                                <div className="custom-select-dropdown">
                                  <div 
                                    className="custom-select-option" 
                                    onClick={() => {
                                      setEditingTax({...editingTax, inclusionRule: 'exclusive'});
                                      setTaxInclusionDropdownOpen(false);
                                    }}
                                  >
                                    Added to Price
                                  </div>
                                  <div 
                                    className="custom-select-option" 
                                    onClick={() => {
                                      setEditingTax({...editingTax, inclusionRule: 'inclusive'});
                                      setTaxInclusionDropdownOpen(false);
                                    }}
                                  >
                                    Included in Price
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="edit-actions">
                              <button
                                className="save-button"
                                onClick={() => {
                                  setTaxRates(taxRates.map(t => 
                                    t.id === editingTax.id ? editingTax : t
                                  ));
                                  setEditingTax(null);
                                }}
                              >
                                Save
                              </button>
                              <button
                                className="cancel-button"
                                onClick={() => setEditingTax(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="tax-info">
                              <div className="tax-name">{tax.name}</div>
                              <div className="tax-details">
                                <span className="tax-rate">{tax.rate}%</span>
                                <span className="tax-type">{tax.type === 'standard' ? 'Standard' : 'Beverage'}</span>
                                <span className="tax-inclusion">{tax.inclusionRule === 'exclusive' ? 'Exclusive' : 'Inclusive'}</span>
                              </div>
                            </div>
                            <div className="tax-actions">
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={tax.isActive}
                                  onChange={() => {
                                    setTaxRates(taxRates.map(t => 
                                      t.id === tax.id ? {...t, isActive: !t.isActive} : t
                                    ));
                                  }}
                                />
                                <span className="slider round"></span>
                              </label>
                              <button
                                className="icon-button edit"
                                onClick={() => setEditingTax(tax)}
                              >
                                Edit
                              </button>
                              <button
                                className="icon-button delete"
                                onClick={async () => {
                                  if (!(await showAppConfirm(`Delete tax "${tax.name}"?`))) return;
                                  setTaxRates(taxRates.filter(t => t.id !== tax.id));
                                }}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Localized Pricing Modal */}
      {showLocalizedPricingModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container full-screen">
            <div className="sp-modal-header">
              <div className="sp-modal-left">
                <button className="sp-close-button sp-back-button" onClick={() => setShowLocalizedPricingModal(false)} aria-label="Back">
                  <FaArrowLeft />
                </button>
                <h2 className="sp-modal-title">Localized Pricing</h2>
              </div>
            </div>
            <div className="localized-pricing-modal-content">
              <div className="localized-pricing-content">
                {/* Global Markup Settings */}
                <div className="pricing-section">
                  <h3 className="subsection-title">Global Markup Settings</h3>
                  <p className="section-description">
                    Set default markup percentages for different sales channels. Dine-In is always treated as the base price.
                  </p>
                  <div className="markup-settings">
                    <div className="markup-field">
                      <label className="form-label">Delivery Platform Markup (%)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={pricingSettings.deliveryMarkup}
                        onChange={(e) => setPricingSettings({...pricingSettings, deliveryMarkup: parseFloat(e.target.value)})}
                        placeholder="20"
                      />
                      <span className="field-hint">For Swiggy, Zomato, etc. to account for commissions</span>
                    </div>
                    <div className="markup-field">
                      <label className="form-label">Takeout Markup (%)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={pricingSettings.takeoutMarkup}
                        onChange={(e) => setPricingSettings({...pricingSettings, takeoutMarkup: parseFloat(e.target.value)})}
                        placeholder="0"
                      />
                      <span className="field-hint">Additional markup for takeout orders</span>
                    </div>
                  </div>
                </div>

                {/* Item-Specific Overrides */}
                <div className="pricing-section">
                  <h3 className="subsection-title">Item-Specific Price Overrides</h3>
                  <p className="section-description">
                    Override global markup rules for specific menu items
                  </p>
                  
                  {/* Search Bar */}
                  <div className="search-container" style={{ marginBottom: '20px' }}>
                    <FaSearch className="search-icon" />
                    <input
                      className="search-input"
                      placeholder="Search menu items..."
                      value={searchItemQuery}
                      onChange={(e) => setSearchItemQuery(e.target.value)}
                    />
                  </div>

                  {/* Item Overrides List */}
                  <div className="item-overrides-list">
                    {itemPriceOverrides
                      .filter(item => item.itemName.toLowerCase().includes(searchItemQuery.toLowerCase()))
                      .map((item) => (
                        <div key={item.id} className="item-override-card" style={{ position: 'relative' }}>
                          <button
                            className="card-close-button"
                            onClick={async () => {
                              if (!(await showAppConfirm(`Remove price override for "${item.itemName}"?`))) return;
                              setItemPriceOverrides(itemPriceOverrides.filter(i => i.id !== item.id));
                            }}
                            style={{
                              position: 'absolute',
                              top: '12px',
                              right: '12px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '20px',
                              color: '#6b7280',
                              padding: '4px',
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                          <div className="item-price-edit-form">
                            <div className="item-name-display">{item.itemName}</div>
                            <div className="price-fields">
                              <div className="price-field">
                                <label>Base (Dine-In)</label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.basePrice}
                                  onChange={(e) => {
                                    const newPrice = parseFloat(e.target.value);
                                    setItemPriceOverrides(itemPriceOverrides.map(i => 
                                      i.id === item.id ? {...i, basePrice: newPrice} : i
                                    ));
                                  }}
                                />
                              </div>
                              <div className="price-field">
                                <label>Takeaway</label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.takeoutPrice}
                                  onChange={(e) => {
                                    const newPrice = parseFloat(e.target.value);
                                    setItemPriceOverrides(itemPriceOverrides.map(i => 
                                      i.id === item.id ? {...i, takeoutPrice: newPrice} : i
                                    ));
                                  }}
                                />
                              </div>
                              <div className="price-field">
                                <label>Delivery</label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.deliveryPrice}
                                  onChange={(e) => {
                                    const newPrice = parseFloat(e.target.value);
                                    setItemPriceOverrides(itemPriceOverrides.map(i => 
                                      i.id === item.id ? {...i, deliveryPrice: newPrice} : i
                                    ));
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restaurant Profile Modal */}
      {showRestaurantProfileModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Restaurant Profile</h2>
              <button className="sp-close-button" onClick={() => setShowRestaurantProfileModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="restaurant-profile-content">
                <div className="profile-form-section">
                  <label className="profile-form-label">Name</label>
                  <input
                    className="profile-form-input"
                    value={restaurantProfile.businessName}
                    onChange={(e) => setRestaurantProfile({...restaurantProfile, businessName: e.target.value})}
                    placeholder="Enter Full Name"
                  />
                </div>

                <div className="profile-form-section">
                  <label className="profile-form-label">Email Address</label>
                  <input
                    className="profile-form-input"
                    value={restaurantProfile.address}
                    onChange={(e) => setRestaurantProfile({...restaurantProfile, address: e.target.value})}
                    placeholder="Enter Email Address"
                    type="email"
                  />
                </div>

                <div className="profile-form-section">
                  <label className="profile-form-label">Phone Number</label>
                  <input
                    className="profile-form-input"
                    value={restaurantProfile.contactDetails}
                    onChange={(e) => setRestaurantProfile({...restaurantProfile, contactDetails: e.target.value})}
                    placeholder="Enter Phone Number"
                    type="tel"
                  />
                </div>

                <div className="spm-actions spm-profile-actions">
                  <button 
                    className="spm-action-button secondary spm-profile-action-button" 
                    onClick={() => setShowRestaurantProfileModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="spm-action-button primary spm-profile-action-button enabled"
                    onClick={() => {
                      alert('Restaurant profile saved!');
                      setShowRestaurantProfileModal(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Locations Modal */}
      {showManageLocationsModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Manage Locations</h2>
              <button className="sp-close-button" onClick={() => setShowManageLocationsModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sp-modal-content">
              <div className="manage-locations-content">
                <p className="section-description" style={{ marginBottom: '20px' }}>
                  Select and manage your restaurant branches
                </p>

                <div className="locations-grid">
                  {locations.map((location) => (
                    <div
                      key={location}
                      className={`location-chip ${selectedLocation === location ? 'selected' : ''}`}
                      onClick={() => setSelectedLocation(location)}
                    >
                      <span className="location-chip-text">{location}</span>
                    </div>
                  ))}
                </div>

                <div className="spm-actions spm-profile-actions" style={{ marginTop: '32px' }}>
                  <button 
                    className="spm-action-button secondary spm-profile-action-button" 
                    onClick={() => setShowManageLocationsModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="spm-action-button primary spm-profile-action-button enabled"
                    onClick={() => {
                      alert(`Location "${selectedLocation}" selected!`);
                      setShowManageLocationsModal(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Portion Multipliers Modal */}
      {showPortionMultipliersModal && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container full-screen">
            <PortionMultiplierSettings 
              onClose={() => setShowPortionMultipliersModal(false)}
            />
          </div>
        </div>
      )}

      {/* Audit Log Modal (Owner Only) */}
      {showAuditLogModal && (userRole?.toLowerCase().includes('owner') || userRole?.toLowerCase().includes('admin')) && (
        <AuditLogViewer 
          businessId={businessId}
          onClose={() => setShowAuditLogModal(false)}
        />
      )}

      {/* Customization Rules Modal */}
      {showCustomizationRulesModal && (
        <CustomizationRules
          businessId={businessId}
          onClose={() => setShowCustomizationRulesModal(false)}
        />
      )}

      {/* Item Mapping Logs Modal */}
      <ItemMappingLogModal
        isOpen={showItemMappingLogModal}
        onClose={() => setShowItemMappingLogModal(false)}
      />

      {/* Piece Guard — pieces-or-packets answers */}
      {showPieceGuardSettings && (
        <PieceGuardSettings
          businessId={businessId}
          onClose={() => setShowPieceGuardSettings(false)}
        />
      )}

      {/* Stock-In Safety Engine — Price history (read only; only reachable while switched on) */}
      {showPriceHistory && cxHistoryOn && (
        <PriceHistorySettings onClose={() => setShowPriceHistory(false)} />
      )}

      {/* Settings › Inventory › Last 5 purchases (read only) */}
      {showLastPurchases && (
        <LastPurchasesSettings onClose={() => setShowLastPurchases(false)} />
      )}

      {alertDialog.open && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container settings-alert-modal">
            <div className="sp-modal-header">
              <h2 className={`sp-modal-title settings-alert-title ${alertDialog.tone}`}>{alertDialog.title}</h2>
            </div>
            <div className="sp-modal-content">
              <p className="settings-alert-message">{alertDialog.message}</p>
              <div className="spm-actions spm-profile-actions settings-dialog-actions">
                <button className="spm-action-button primary spm-profile-action-button" onClick={hideAppAlert}>OK</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDialog.open && (
        <div className="sp-modal-overlay">
          <div className="sp-modal-container settings-alert-modal">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Confirm</h2>
            </div>
            <div className="sp-modal-content">
              <p className="settings-alert-message">{confirmDialog.message}</p>
              <div className="spm-actions spm-profile-actions settings-dialog-actions">
                <button className="spm-action-button secondary spm-profile-action-button" onClick={() => handleConfirmChoice(false)}>Cancel</button>
                <button className="spm-action-button primary spm-profile-action-button" onClick={() => handleConfirmChoice(true)}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
